// src/components/admin/AssetManagement.js

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ToastNotification          from "../../components/common/ToastNotification";
import DeleteConfirmationModal    from "../../components/Modals/DeleteConfirmationModal";
import AssetQRModal               from "../../components/Modals/AssetQRModal";
import ExtendWarrantyModal        from "../../components/Modals/ExtendWarrantyModal";
import DisposeAssetModal          from "../../components/Modals/DisposeAssetModal";
import QRPrintModal               from "../../components/Modals/QRPrintModal";
import QuantityAdjustmentModal    from "../../components/Modals/QuantityAdjustmentModal";
import ReturnAssetModal           from "../../components/Modals/ReturnAssetModal";
import ConsumableStockModal       from "../../components/Modals/ConsumableStockModal";
import AssignAssetModal           from "../../components/Modals/AssignAssetModal";
import CreateEditAssetModal       from "../../components/Modals/CreateEditAssetModal";
import ApprovalHistoryModal       from "../../components/Modals/ApprovalHistoryModal";
import AssetImportModal from "../../components/Modals/AssetImportModal";
import { assetImportService } from "../../services/assetImportService";
import NewTicketForm            from "../user/NewTicketForm"; 
import { useAuth }                from "../../contexts/AuthContext";
import { useNavigate }            from "react-router-dom";
import PermissionDenied           from "../../components/common/PermissionDenied";
import { logAuditEvent }          from "../../utils/logAudit";
import "./ManagementPage.css";
import { helpDeskService, ROLES } from "../../services/helpDeskService";
import { assetService }           from "../../services/assetService";
import { categoryService }        from "../../services/categoryService";
import { subCategoryService }     from "../../services/subCategoryService";
import { brandService }           from "../../services/brandService";
import { vendorService }          from "../../services/vendorService";
import { departmentService }      from "../../services/departmentService";
import { userService }            from "../../services/userService";
import { locationService }        from "../../services/locationService";
import { preferencesService }     from "../../services/preferencesService";
import { singleValueListService } from "../../services/singleValueListService";

import {
  ASSETS_PAGE_SIZE_PREF_TYPE,
  ASSETS_PAGE_SIZE_PREF_NAME,
} from "../common/SettingsPage";
import {
  
  ASSET_TYPES_SHOW_PARENT,
  SERIALIZATION_TYPES,
  getAssetDepreciation,
  getDepreciationLabel,
  isAssetAssigned,
  canRecordConsumption,
  isReturnOnlyAsset,
  canBeDisposed,
 
  buildAssignPayload,
  buildReleasePayload,
 
} from "../../utils/assetUtils";

import {
  safeTimestampToDate,
  formatDate,
  isExpired as isWarrantyExpired,
  isExpiringSoon as isWarrantyExpiring,
} from "../../utils/dateUtils";
import {
  deriveApprovalStatus,
  isScanAllowedByApproval,
} from "../../utils/approvalUtils";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  Chip,
  Alert,
  Fade,
  Divider,
  InputAdornment,
  Tooltip,
  Collapse,
  Skeleton,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import AddIcon               from "@mui/icons-material/Add";
import SearchIcon            from "@mui/icons-material/Search";
import FileDownloadIcon      from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon      from "@mui/icons-material/PictureAsPdf";
import QrCodeIcon            from "@mui/icons-material/QrCode";
import MoreVertIcon          from "@mui/icons-material/MoreVert";
import EditIcon              from "@mui/icons-material/Edit";
import DeleteIcon            from "@mui/icons-material/Delete";
import UpdateIcon            from "@mui/icons-material/Update";
import HistoryIcon           from "@mui/icons-material/History";
import DeleteSweepIcon       from "@mui/icons-material/DeleteSweep";
import DescriptionIcon       from "@mui/icons-material/Description";
import ClearIcon             from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FilterAltIcon         from "@mui/icons-material/FilterAlt";
import CloseIcon             from "@mui/icons-material/Close";
import PrintIcon             from "@mui/icons-material/Print";
import AssignmentReturnIcon  from "@mui/icons-material/AssignmentReturn";
import InventoryIcon         from "@mui/icons-material/Inventory";
import SwapHorizIcon         from "@mui/icons-material/SwapHoriz";
import SettingsIcon          from "@mui/icons-material/Settings";
import AssignmentIndIcon     from "@mui/icons-material/AssignmentInd";
import LocationOnIcon        from "@mui/icons-material/LocationOn";
import ViewColumnIcon        from "@mui/icons-material/ViewColumn";
import RestartAltIcon        from "@mui/icons-material/RestartAlt";
import HowToRegIcon          from "@mui/icons-material/HowToReg";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";   
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const DEFAULT_PAGE_SIZE    = 50;
const WARRANTY_EXPIRY_DAYS = 30;

let pdfLibsPromise = null;

const resolveJsPDF = (mod) => {
  if (!mod) return null;
  if (typeof mod.jsPDF === "function") return mod.jsPDF;
  if (typeof mod.default === "function") return mod.default;
  if (typeof mod.default?.jsPDF === "function") return mod.default.jsPDF;
  if (typeof mod === "function") return mod;
  return null;
};

const resolveAutoTable = (mod) => {
  if (!mod) return null;
  if (typeof mod.default === "function") return mod.default;
  if (typeof mod.autoTable === "function") return mod.autoTable;
  if (typeof mod === "function") return mod;
  if (typeof mod.default?.autoTable === "function") return mod.default.autoTable;
  return null;
};

const getPDFLibraries = () => {
  if (!pdfLibsPromise) {
    pdfLibsPromise = Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ])
      .then(([jsPDFModule, autoTableModule]) => {
        const jsPDF = resolveJsPDF(jsPDFModule);
        const autoTable = resolveAutoTable(autoTableModule);

        if (!jsPDF) {
          throw new Error("Could not resolve jsPDF constructor.");
        }
        if (!autoTable && typeof jsPDF.API?.autoTable !== "function") {
          throw new Error("Could not resolve jspdf-autotable function.");
        }

        return { jsPDF, autoTable };
      })
      .catch((err) => {
        pdfLibsPromise = null;
        throw err;
      });
  }
  return pdfLibsPromise;
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(amount || 0);

// ── Approval status derivation ──

const getApprovalStatusForAsset = (asset, svlApprovers = []) => {
  if (!asset) return "NA";
  if (!Array.isArray(asset.approverList) || asset.approverList.length === 0) {
    return "NA";
  }

  // Check for Returned / Rejected
  const isReturned = asset.approverList.some((r) => {
    const s = String(r?.status || "").toLowerCase();
    return s.includes("reject") || s.includes("return") || s.includes("correction");
  });
  if (isReturned) return "Returned";

  const sorted = [...asset.approverList].sort((a, b) => Number(a.level) - Number(b.level));

  // Find the highest level that has acted (not "pending")
  let latestActed = null;
  for (let i = sorted.length - 1; i >= 0; i--) {
    const status = String(sorted[i].status || "").toLowerCase();
    if (status && !status.includes("pending")) {
      latestActed = sorted[i];
      break;
    }
  }

  if (!latestActed) return "Draft";

  const maxLevel = Math.max(...sorted.map((r) => Number(r.level)));
  if (Number(latestActed.level) === maxLevel) {
    return "Approved";
  }

  const rawStatus = String(latestActed.status || "");
  // If the backend saved "Approved Level 1", translate it to the SVL status
  if (rawStatus.toLowerCase().startsWith("approved level")) {
    const svlRow = svlApprovers.find(
      (s) => Number(s.level) === Number(latestActed.level)
    );
    if (svlRow && svlRow.status) {
      return svlRow.status; // Returns "Checked", "Reviewed", etc.
    }
  }

  return rawStatus;
};
const isAssetReturned = (asset) => {
  const label = getApprovalStatusForAsset(asset);
  return String(label).toLowerCase() === "returned";
};


const getApprovalStatusColor = (label) => {
  const s = String(label || "").toLowerCase();
  if (s === "na" || s === "" || s === "—") return "default";
  if (s === "draft")          return "info";      
  if (s === "returned")       return "warning";  
  if (s === "rejected")       return "error";
  if (s === "correction")     return "warning";
  if (s === "pending")        return "warning";
  if (s.includes("approved")) return "success";   
  return "primary";                              
};


const isApprovalLocked = (asset) => {
  const label = String(getApprovalStatusForAsset(asset) || "").toLowerCase();
  if (!label || label === "na")     return false;
  if (label === "draft")            return false;
  if (label === "returned")         return false;
  if (label === "approved")         return false;
  return true; 
};

const canRaiseTicketForAsset = (asset) => {
  if (!asset) return false;

  // Disposed assets have nothing to fix
  const status = String(asset.status || "").toLowerCase();
  if (status === "disposed") return false;


  if (!(asset.assetId || asset.id)) return false;

  const label = getApprovalStatusForAsset(asset);
  if (label !== "NA" && !isScanAllowedByApproval(asset)) return false;

  return true;
};

const COLUMNS = [
  { key: "checkbox",           label: null,                     skeletonWidth: 18,  align: "center", isCheckbox: true },
  { key: "assetId", label: "Asset ID / Actions", sortable: true, align: "left" },
  { key: "assetName",          label: "Asset Name",             skeletonWidth: 100, align: "left"   },
  { key: "assetType",          label: "Asset Type",             skeletonWidth: 90,  align: "left"   },
  { key: "ownershipType",      label: "Ownership Type",         skeletonWidth: 80,  align: "left"   },
  { key: "parentAsset",        label: "Parent Asset",           skeletonWidth: 100, align: "left"   },
  { key: "category",           label: "Category",               skeletonWidth: 120, align: "left"   },
  { key: "location",           label: "Location",               skeletonWidth: 120, align: "left"   },
  { key: "tracking",           label: "Tracking",               skeletonWidth: 80,  align: "left"   },
  { key: "purchaseDate",       label: "Purchase Date",          skeletonWidth: 100, align: "left"   },
  { key: "purchaseValue",      label: "Purchase Value (INR)",   skeletonWidth: 110, align: "right"  },
  { key: "manufacturerSerial", label: "Manufacturer Serial ID", skeletonWidth: 150, align: "left"   },
  { key: "quantity",           label: "Quantity",               skeletonWidth: 70,  align: "center" },
  { key: "warranty",           label: "Warranty",               skeletonWidth: 100, align: "left"   },
    { key: "status",             label: "Status",                 skeletonWidth: 90,  align: "left"   },
  { key: "tagged",             label: "Tagged",                 skeletonWidth: 80,  align: "center" },
  { key: "approvalStatus",     label: "Approval Status",        skeletonWidth: 120, align: "left"   }, 
 
];

const DATA_COLUMNS  = COLUMNS.filter((c) => !c.isCheckbox);
const SKELETON_ROWS = 8;

/* ── COLUMN PREFERENCE CONSTANTS ─────────────────────────────────────────── */
const ALWAYS_VISIBLE_COLUMNS  = ["checkbox", "assetId", "assetName"];
const COLUMN_PREF_STORAGE_KEY = "assetManagement_visibleColumns";
const DEFAULT_VISIBLE_COLUMNS = COLUMNS.map((c) => c.key);

const HeaderSkeleton = () => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
    <Box>
      <Skeleton variant="text" width={340} height={44} sx={{ borderRadius: 1 }} />
      <Skeleton variant="text" width={280} height={20} sx={{ borderRadius: 1, mt: 0.5 }} />
    </Box>
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
      {[160, 120, 110].map((w, i) => (
        <Skeleton key={i} variant="rounded" width={w} height={36} sx={{ borderRadius: 2 }} />
      ))}
    </Box>
  </Box>
);

const SearchBarSkeleton = () => (
  <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
    <Skeleton variant="rounded" width="33%" height={40} sx={{ borderRadius: 2 }} />
  </Paper>
);

const SkeletonTableRow = ({ rowIdx, columns = DATA_COLUMNS }) => (
  <TableRow key={rowIdx}>
    <TableCell padding="checkbox" align="center">
      <Skeleton variant="rounded" width={18} height={18} />
    </TableCell>
    {columns.map((col) => {
      if (col.key === "category") {
        return (
          <TableCell key={col.key}>
            <Skeleton variant="text" width={90} height={18} sx={{ borderRadius: 1 }} />
            <Skeleton variant="text" width={70} height={14} sx={{ borderRadius: 1, mt: 0.3 }} />
          </TableCell>
        );
      }
      if (["tracking", "warranty", "status", "assetType", "ownershipType"].includes(col.key)) {
        return (
          <TableCell key={col.key} align={col.align}>
            <Skeleton
              variant="rounded" width={col.skeletonWidth} height={22}
              sx={{ borderRadius: 10, ...(col.align === "center" ? { mx: "auto" } : {}) }}
            />
          </TableCell>
        );
      }
      if (col.key === "purchaseValue") {
        return (
          <TableCell key={col.key} align="right">
            <Skeleton variant="text" width={col.skeletonWidth} height={20}
              sx={{ borderRadius: 1, ml: "auto" }} />
          </TableCell>
        );
      }
      if (col.key === "actions") {
        return (
          <TableCell key={col.key} align="center">
            <Skeleton variant="circular" width={28} height={28} />
          </TableCell>
        );
      }
      return (
        <TableCell key={col.key} align={col.align}>
          <Skeleton variant="text" width={col.skeletonWidth} height={20}
            sx={{ borderRadius: 1, ...(col.align === "center" ? { mx: "auto" } : {}) }}
          />
        </TableCell>
      );
    })}
  </TableRow>
);

const TableSkeleton = () => (
  <TableContainer
    component={Paper} variant="outlined"
    sx={{
      borderRadius: 3, overflowX: "auto",
      "&::-webkit-scrollbar":       { height: 8 },
      "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.12)", borderRadius: 4 },
    }}
  >
    <Table sx={{ minWidth: 1400 }} size="medium">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" align="center" sx={{ bgcolor: "background.neutral" }}>
            <Skeleton variant="rounded" width={18} height={18} />
          </TableCell>
          {DATA_COLUMNS.map((col) => (
            <TableCell key={col.key} align={col.align} sx={{ bgcolor: "background.neutral" }}>
              <Skeleton variant="text" width={col.skeletonWidth} height={20} sx={{ borderRadius: 1 }} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
          <SkeletonTableRow key={i} rowIdx={i} />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const PaginationSkeleton = () => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
    <Skeleton variant="text"    width={180} height={18} sx={{ borderRadius: 1 }} />
    <Skeleton variant="rounded" width={320} height={36} sx={{ borderRadius: 2 }} />
  </Box>
);

const AssetManagementSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}>
    <HeaderSkeleton />
    <SearchBarSkeleton />
    <TableSkeleton />
    <PaginationSkeleton />
  </Container>
);

/* ── COLUMN PREFERENCES MODAL ────────────────────────────────────────────── */
const ColumnPreferencesModal = ({
  open,
  onClose,
  visibleColumns,
  onToggleColumn,
  onShowAll,
  onHideAll,
  onReset,
}) => {
  const configurableColumns = COLUMNS.filter(
    (c) => !["checkbox", "actions"].includes(c.key)
  );

  const visibleCount = configurableColumns.filter((c) =>
    visibleColumns.includes(c.key)
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <ViewColumnIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            Column Preferences
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Choose which columns to display in the assets table
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
            px: 0.5,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            <strong>{visibleCount}</strong> of {configurableColumns.length} columns visible
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" onClick={onShowAll}
              sx={{ textTransform: "none", fontWeight: 600 }}>
              Show All
            </Button>
            <Button size="small" color="inherit" onClick={onHideAll}
              sx={{ textTransform: "none", fontWeight: 600 }}>
              Hide All
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <FormGroup>
          <Grid container spacing={0.5}>
            {configurableColumns.map((col) => {
              const isVisible = visibleColumns.includes(col.key);
              const isLocked  = ALWAYS_VISIBLE_COLUMNS.includes(col.key);
              return (
                <Grid item xs={12} sm={6} key={col.key}>
                  <FormControlLabel
                    disabled={isLocked}
                    control={
                      <Switch
                        checked={isVisible}
                        onChange={() => onToggleColumn(col.key)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {col.label}
                        </Typography>
                        {isLocked && (
                          <Chip label="Required" size="small"
                            sx={{ height: 16, fontSize: "0.6rem", fontWeight: 700 }} />
                        )}
                      </Box>
                    }
                    sx={{
                      m: 0, py: 0.5, px: 1, width: "100%", borderRadius: 1.5,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </FormGroup>

        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          <Typography variant="caption">
            Asset ID, Asset Name and Actions are always visible. Your preferences
            are saved automatically on this device.
          </Typography>
        </Alert>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onReset} startIcon={<RestartAltIcon />}
          sx={{ textTransform: "none", fontWeight: 600 }}>
          Reset to Default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="contained" disableElevation
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── HOOKS ───────────────────────────────────────────────────────────────── */
const useColumnPreferences = () => {
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const stored = localStorage.getItem(COLUMN_PREF_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const valid = parsed.filter((k) => DEFAULT_VISIBLE_COLUMNS.includes(k));
          return Array.from(new Set([...ALWAYS_VISIBLE_COLUMNS, ...valid]));
        }
      }
    } catch {
      /* fall through to defaults */
    }
    return DEFAULT_VISIBLE_COLUMNS;
  });

  const persist = useCallback((cols) => {
    try {
      localStorage.setItem(COLUMN_PREF_STORAGE_KEY, JSON.stringify(cols));
    } catch { /* ignore quota errors */ }
  }, []);

  const toggleColumn = useCallback(
    (columnKey) => {
      if (ALWAYS_VISIBLE_COLUMNS.includes(columnKey)) return;
      setVisibleColumns((prev) => {
        const next = prev.includes(columnKey)
          ? prev.filter((k) => k !== columnKey)
          : [...prev, columnKey];
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const showAllColumns = useCallback(() => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    persist(DEFAULT_VISIBLE_COLUMNS);
  }, [persist]);

  const hideAllOptionalColumns = useCallback(() => {
    setVisibleColumns(ALWAYS_VISIBLE_COLUMNS);
    persist(ALWAYS_VISIBLE_COLUMNS);
  }, [persist]);

  const resetColumns = useCallback(() => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    persist(DEFAULT_VISIBLE_COLUMNS);
  }, [persist]);

  return {
    visibleColumns,
    toggleColumn,
    showAllColumns,
    hideAllOptionalColumns,
    resetColumns,
  };
};

const useAssetsPageSize = (currentUser) => {
  const [defaultPageSize, setDefaultPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [prefLoading,     setPrefLoading]     = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!currentUser?.uid && !currentUser?.id) { setPrefLoading(false); return; }
      try {
        const allPrefs = await preferencesService.getAllIncludingSystem(currentUser);
        if (cancelled) return;

        const found = allPrefs.find(
          (p) =>
            p.preferenceType === ASSETS_PAGE_SIZE_PREF_TYPE &&
            p.preferenceName === ASSETS_PAGE_SIZE_PREF_NAME &&
            p.deleted !== true
        );

        if (found) {
          const val = parseInt(found.preferenceValue, 10);
          setDefaultPageSize(isNaN(val) ? DEFAULT_PAGE_SIZE : val);
        }
      } catch {
        // Silently fall back to default
      } finally {
        if (!cancelled) setPrefLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentUser]);

  return { defaultPageSize, prefLoading };
};

const useAssetData = (currentUser, showNotification, navigate) => {
  const [assets,          setAssets]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [permissionError, setPermissionError] = useState(null);
  const redirectTimerRef                      = useRef(null);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setPermissionError(null);

    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }

    try {
      //  `fetchAssets` resolves currentUser from localStorage.
      const assetsData = await assetService.fetchAssets({});
      setAssets(assetsData || []);
    } catch (error) {
      const msg = error?.message || "";
      const isLocalDbFailure =
        msg.includes("Network Error") ||
        msg.includes("Failed to fetch") ||
        msg.includes("PGRST");

      if (isLocalDbFailure) {
        localStorage.removeItem("localServerVerified");
        showNotification(
          "Local database connection failure detected. Redirecting...",
          "error"
        );
        redirectTimerRef.current = setTimeout(
          () => navigate("/server-check"),
          1500
        );
        return;
      }
      setPermissionError(msg);
      showNotification(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification, navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  return {
    assets, setAssets, loading, permissionError, setPermissionError, fetchAssets,
  };
};

const useMasterData = (currentUser, showNotification) => {
  const [categories,          setCategories]         = useState([]);
  const [subCategories,       setSubCategories]       = useState([]);
  const [masterCategories,    setMasterCategories]    = useState([]);
  const [masterSubCategories, setMasterSubCategories] = useState([]);
  const [masterBrands,        setMasterBrands]        = useState([]);
  const [masterVendors,       setMasterVendors]       = useState([]);
  const [masterDepartments,   setMasterDepartments]   = useState([]);
  const [systemUsers,         setSystemUsers]         = useState([]);
  const [masterLocations,     setMasterLocations]     = useState([]);
  const [loadingMasterData,   setLoadingMasterData]   = useState(true);
  const [masterDataError,     setMasterDataError]     = useState("");

 const fetchAllMasterData = useCallback(async () => {
    setLoadingMasterData(true);
    setMasterDataError("");

    if (!currentUser?.role) {
      setMasterDataError("User role not found. Please log in again.");
      setLoadingMasterData(false);
      return;
    }

const companyId = currentUser?.companyId || "";

const results = await Promise.allSettled([
  categoryService.fetchCategories({ companyId }),
  subCategoryService.fetchSubCategories({ companyId }),
  brandService.fetchBrands({ companyId }),
  vendorService.fetchVendors({ companyId }),
  departmentService.fetchDepartments({ companyId }),
  userService.fetchUsers(companyId),
  locationService.fetchLocations({ companyId }),
]);

    const [
      catsResult, subCatsResult, brandsResult,
      vendorsResult, deptsResult, usersResult, locsResult,
    ] = results;

    const getValue = (result, fallback = []) =>
      result.status === "fulfilled" ? result.value : fallback;

    const cats    = getValue(catsResult);
    const subCats = getValue(subCatsResult);
    const brands  = getValue(brandsResult);
    const vendors = getValue(vendorsResult);
    const depts   = getValue(deptsResult);
    const users   = getValue(usersResult);
    const locs    = getValue(locsResult);

    results.forEach((r, i) => {
      if (r.status === "rejected")
        console.warn(`Master data fetch [${i}] failed:`, r.reason);
    });

    if (results.some((r) => r.status === "rejected")) {
      showNotification(
        "Some reference data failed to load. Asset creation may be limited.",
        "warning"
      );
    }

    setCategories(cats.map((c) => c.categoryName || c.name).filter(Boolean));
    setSubCategories(subCats.map((sc) => sc.subCategoryName || sc.name).filter(Boolean));
    setMasterCategories(
      cats.map((c) => ({ id: c.id, name: c.categoryName || c.name, code: c.categoryId, ...c }))
    );
    setMasterSubCategories(
      subCats.map((sc) => ({
        id: sc.id, name: sc.subCategoryName || sc.name, categoryId: sc.categoryId, ...sc,
      }))
    );
    setMasterBrands(
      brands.map((b) => ({ id: b.id, name: b.name || b.brandName, ...b }))
    );
    setMasterVendors(
      vendors.map((v) => ({ id: v.id, name: v.vendorName, ...v }))
    );
    setMasterDepartments(depts.map((d) => ({ id: d.id, ...d })));
    setSystemUsers(
      users.map((u) => ({ id: u.id, ...u, displayName: u.displayName || u.email }))
    );

    const isLocationAdmin  = currentUser?.role === "locationAdmin";
    const userLocationCode = currentUser?.locationCode;
    setMasterLocations(
      isLocationAdmin && userLocationCode
        ? locs.filter((l) => l.locationCode === userLocationCode)
        : locs
    );

    setLoadingMasterData(false);
  }, [currentUser, showNotification]);

  return {
    categories, subCategories,
    masterCategories, masterSubCategories,
    masterBrands, masterVendors, masterDepartments,
    systemUsers, masterLocations,
    loadingMasterData, masterDataError, setMasterDataError,
    fetchAllMasterData,
  };
};

const useAssetFilters = (assets, svlApprovers = []) => {
  const [searchTerm,           setSearchTerm]          = useState("");
  const [activeFilter,         setActiveFilter]         = useState("all");
  const [selectedCategory,     setSelectedCategory]     = useState("all");
  const [selectedSubCategory,  setSelectedSubCategory]  = useState("all");
  const [selectedLocation,     setSelectedLocation]     = useState("all");
  const [serializationFilter,  setSerializationFilter]  = useState("all");
  const [warrantyExpiryFilter, setWarrantyExpiryFilter] = useState("all");
  const [olderThanValue,       setOlderThanValue]       = useState("");
  const [olderThanUnit,        setOlderThanUnit]        = useState("years");
  const [showFilters,          setShowFilters]          = useState(false);
   const [assetTypeFilter,      setAssetTypeFilter]      = useState("all");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("all"); 
  const [ownershipTypeFilter,  setOwnershipTypeFilter]  = useState("all");
  const [taggedFilter,         setTaggedFilter]         = useState("all"); 
  const [sortField,            setSortField]            = useState("none");
  const [sortDirection,        setSortDirection]        = useState("asc");

  const resetFilters = useCallback(() => {
    setSearchTerm("");
    setActiveFilter("all");
    setSelectedCategory("all");
    setSelectedSubCategory("all");
    setSelectedLocation("all");
    setSerializationFilter("all");
    setWarrantyExpiryFilter("all");
    setOlderThanValue("");
    setOlderThanUnit("years");
    setAssetTypeFilter("all");
    setOwnershipTypeFilter("all");
      setApprovalStatusFilter("all"); 
      setTaggedFilter("all");
    setSortField("none");
    setSortDirection("asc");
  }, []);

  const filteredAssets = useMemo(() => {
    let result = [...assets];

   if (searchTerm.trim()) {
  const term = searchTerm.toLowerCase().trim();
  const matches = (val) =>
    val != null && String(val).toLowerCase().includes(term);

  result = result.filter(
    (asset) =>
      matches(asset.assetName)                ||
      matches(asset.assetId)                  ||
      matches(asset.categoryName)             ||
      matches(asset.subCategoryName)          ||
      matches(asset.brandName)                ||
      matches(asset.status)                   ||
      matches(asset.currentLocationName)      ||
      matches(asset.locationCode)             ||
      matches(asset.manufacturerSerialNumber) ||
      matches(asset.serialNumber)             ||
      matches(asset.assetType)                ||
      matches(asset.ownershipType)            ||
      matches(asset.parentAssetName)
  );
}
switch (activeFilter) {
  case "available":
    result = result.filter(
      (a) => String(a.status || "").toLowerCase() === "available"
    );
    break;
  case "assigned":
    result = result.filter(
      (a) => a.assignedTo || (a.assignedToName && a.assignedToName !== "Unassigned")
    );
    break;
  case "maintenance":
    result = result.filter((a) => {
      const s = String(a.status || "").toLowerCase();
      return s === "in-maintenance" || s === "in maintenance" || s === "maintenance";
    });
    break;
  case "repair":
    result = result.filter((a) => {
      const s = String(a.status || "").toLowerCase();
      return s === "under-repair" || s === "under repair" || s === "repair";
    });
    break;
  case "reserved":
    result = result.filter(
      (a) => String(a.status || "").toLowerCase() === "reserved"
    );
    break;
  case "disposed":
    result = result.filter((a) => {
      const s = String(a.status || "").toLowerCase();
      return s === "disposed" || a.disposalDate;
    });
    break;
  case "lost":
    result = result.filter((a) => {
      const s = String(a.status || "").toLowerCase();
      return s === "lost" || s === "stolen" || s === "lost/stolen" || s === "lost / stolen";
    });
    break;
  default:
    break;
}

    if (serializationFilter === "serialized") {
      result = result.filter(
        (a) => a.serializationType === SERIALIZATION_TYPES.SERIALIZED || !a.serializationType
      );
    } else if (serializationFilter === "non-serialized") {
      result = result.filter(
        (a) => a.serializationType === SERIALIZATION_TYPES.NON_SERIALIZED
      );
    }

    switch (warrantyExpiryFilter) {
      case "expiring":
        result = result.filter(
          (a) => isWarrantyExpiring(a.warrantyExpiry, WARRANTY_EXPIRY_DAYS)
        );
        break;
      case "expired":
        result = result.filter((a) => isWarrantyExpired(a.warrantyExpiry));
        break;
      case "active":
        result = result.filter(
          (a) => a.warrantyExpiry && !isWarrantyExpired(a.warrantyExpiry)
        );
        break;
      default:
        break;
    }

    if (selectedCategory !== "all") {
      result = result.filter((a) => a.categoryName === selectedCategory);
    }
    if (selectedSubCategory !== "all") {
      result = result.filter((a) => a.subCategoryName === selectedSubCategory);
    }
     if (selectedLocation !== "all") {
      result = result.filter(
        (a) => String(a.locationCode || "").trim() === String(selectedLocation).trim()
      );
    }

    if (olderThanValue !== "" && Number(olderThanValue) > 0) {
      const val    = parseInt(olderThanValue, 10);
      const cutoff = new Date();
      if (olderThanUnit === "years") cutoff.setFullYear(cutoff.getFullYear() - val);
      else cutoff.setMonth(cutoff.getMonth() - val);
      result = result.filter((asset) => {
        const purchaseDate = safeTimestampToDate(
          asset.purchaseDate || asset.purchasedDate || asset.createdAt
        );
        return purchaseDate ? purchaseDate <= cutoff : false;
      });
    }

 if (assetTypeFilter !== "all") {
  const wanted = String(assetTypeFilter).trim().toLowerCase();
  result = result.filter(
    (a) => String(a.assetType || "").trim().toLowerCase() === wanted
  );
}
if (ownershipTypeFilter !== "all") {
  const wanted = String(ownershipTypeFilter).trim().toLowerCase();

  // Normalise common synonyms so "Leased"/"lease"/"Leasing" all match,
  // and blank/undefined ownership defaults to "owned".
  const normalizeOwnership = (raw) => {
    const v = String(raw || "").trim().toLowerCase();
    if (!v) return "owned";                       // default when field is empty
    if (v.startsWith("leas")) return "leased";    // leased, lease, leasing
    if (v.startsWith("rent")) return "rented";    // rented, rent, renting
    if (v.startsWith("own"))  return "owned";     // owned, own
    return v;
  };

  const wantedNorm = normalizeOwnership(wanted);

  result = result.filter(
    (a) => normalizeOwnership(a.ownershipType) === wantedNorm
  );
}
if (approvalStatusFilter !== "all") {
  result = result.filter((a) => {
   const label = getApprovalStatusForAsset(a, svlApprovers);
    return String(label).toLowerCase() === String(approvalStatusFilter).toLowerCase();
  });
}


if (taggedFilter !== "all") {
  const isTaggedWanted = taggedFilter === "yes";
  result = result.filter((a) => Boolean(a.isTagged) === isTaggedWanted);
}
     // Comprehensive Universal Sorting Logic
    if (sortField !== "none") {
      result = [...result].sort((a, b) => {
        let actualField = sortField;

        if (sortField === "category")           actualField = "categoryName";
        if (sortField === "tracking")           actualField = "serializationType";
        if (sortField === "manufacturerSerial") actualField = "manufacturerSerialNumber";
        if (sortField === "warranty")           actualField = "warrantyExpiry";
        if (sortField === "parentAsset")        actualField = "parentAssetName";

        let aVal = a[actualField];
        let bVal = b[actualField];

        // ── Normalise values into either numbers or strings — never
        //    leave a raw value that isn't safe to call .toLowerCase() on.
        if (sortField === "purchaseValue") {
          aVal = Number(a.purchaseValue || a.leaseAmount || a.rentAmount || 0);
          bVal = Number(b.purchaseValue || b.leaseAmount || b.rentAmount || 0);
        } else if (sortField === "purchaseDate") {
          const dA = safeTimestampToDate(a.purchaseDate || a.purchasedDate);
          const dB = safeTimestampToDate(b.purchaseDate || b.purchasedDate);
          aVal = dA ? dA.getTime() : 0;
          bVal = dB ? dB.getTime() : 0;
        } else if (sortField === "warranty" || sortField === "warrantyExpiry") {
          const dA = safeTimestampToDate(a.warrantyExpiry);
          const dB = safeTimestampToDate(b.warrantyExpiry);
          aVal = dA ? dA.getTime() : 0;
          bVal = dB ? dB.getTime() : 0;
        } else if (sortField === "quantity") {
          aVal = Number(a.quantity || 1);
          bVal = Number(b.quantity || 1);
        } else {
          // ── Defensive: coerce anything that isn't a primitive number
          //    into a string so `.toLowerCase()` is always safe.
          //    Handles: numbers, Dates, Firestore Timestamps, objects.
          const coerce = (v) => {
            if (v == null) return "";
            if (typeof v === "string") return v;
            if (typeof v === "number" || typeof v === "boolean") return String(v);
            // Firestore Timestamp / Date / anything with toString()
            if (typeof v?.toDate === "function") {
              try { return v.toDate().toISOString(); } catch { return ""; }
            }
            if (v instanceof Date) return v.toISOString();
            if (typeof v === "object") {
              // e.g. { seconds, nanoseconds } Firestore shape
              if (typeof v.seconds === "number") {
                return new Date(v.seconds * 1000).toISOString();
              }
              try { return JSON.stringify(v); } catch { return ""; }
            }
            return String(v);
          };

          aVal = coerce(aVal).toLowerCase();
          bVal = coerce(bVal).toLowerCase();
        }

        if (aVal === bVal) return 0;
        if (aVal == null || aVal === "") return sortDirection === "asc" ? 1 : -1;
        if (bVal == null || bVal === "") return sortDirection === "asc" ? -1 : 1;

        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal);
        const bStr = String(bVal);

        return sortDirection === "asc"
          ? aStr.localeCompare(bStr, undefined, { numeric: true })
          : bStr.localeCompare(aStr, undefined, { numeric: true });
      });
    }

    return result;
  }, [
    assets, searchTerm, activeFilter, selectedCategory, selectedSubCategory,
    selectedLocation, warrantyExpiryFilter, serializationFilter,
    olderThanValue, olderThanUnit, assetTypeFilter, ownershipTypeFilter,
       sortField, sortDirection, approvalStatusFilter, taggedFilter
  ]);

  return {
    filteredAssets,
    searchTerm,           setSearchTerm,
    activeFilter,         setActiveFilter,
    selectedCategory,     setSelectedCategory,
    selectedSubCategory,  setSelectedSubCategory,
    selectedLocation,     setSelectedLocation,
    serializationFilter,  setSerializationFilter,
    warrantyExpiryFilter, setWarrantyExpiryFilter,
    olderThanValue,       setOlderThanValue,
    olderThanUnit,        setOlderThanUnit,
    showFilters,          setShowFilters,
    resetFilters,
    assetTypeFilter,      setAssetTypeFilter,
    ownershipTypeFilter,  setOwnershipTypeFilter,
    sortField,            setSortField,
    sortDirection,        setSortDirection,
    approvalStatusFilter, setApprovalStatusFilter,
    taggedFilter,         setTaggedFilter,
  };
};

/* ── MAIN COMPONENT ──────────────────────────────────────────────────────── */
const AssetManagement = () => {
  const navigate                              = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();

  const [notification,       setNotification]  = useState(null);
  const notificationTimerRef                   = useRef(null);

  const showNotification = useCallback((message, type = "success") => {
    if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
    setNotification({ message, type });
    notificationTimerRef.current = setTimeout(() => setNotification(null), 5000);
  }, []);

  useEffect(() => {
    return () => {
      if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
    };
  }, []);

  const {
    assets, setAssets, loading, permissionError, setPermissionError, fetchAssets,
  } = useAssetData(currentUser, showNotification, navigate);

  const {
    categories, subCategories,
    masterCategories, masterSubCategories,
    masterBrands, masterVendors, masterDepartments,
    systemUsers, masterLocations,
    loadingMasterData, masterDataError, setMasterDataError,
    fetchAllMasterData,
  } = useMasterData(currentUser, showNotification);

    const enrichedAssets = useMemo(() => {
    const catMap = new Map(
      masterCategories.map((c) => [c.id || c.categoryId, c.name || c.categoryName])
    );
    const subCatMap = new Map(
      masterSubCategories.map((sc) => [sc.id || sc.subCategoryId, sc.name || sc.subCategoryName])
    );
    const brndMap = new Map(
      masterBrands.map((b) => [b.id || b.brandId, b.name || b.brandName])
    );

    // Safely build location map
    const locMap = new Map(
      masterLocations.map((l) => [
        String(l.locationCode || "").trim(),
        String(l.locationName || l.name || l.locationCode || "").trim(),
      ])
    );

    return assets.map((a) => ({
      ...a,
      categoryName:    catMap.get(a.categoryId)       || a.categoryName    || "",
      subCategoryName: subCatMap.get(a.subCategoryId) || a.subCategoryName || "",
      brandName:       brndMap.get(a.brandId)         || a.brandName       || "",
      // Ensure location name is populated correctly
      locationName: 
        locMap.get(String(a.locationCode || "").trim()) ||
        a.locationName ||
        a.currentLocationName ||
        "",
    }));
  }, [assets, masterCategories, masterSubCategories, masterBrands, masterLocations]); 
const [prefsVersion, setPrefsVersion] = useState(0);
const [ownershipOn, setOwnershipOn] = useState(false);
const [bypassEmails, setBypassEmails] = useState([]);
const [catalogAssetTypes,     setCatalogAssetTypes]     = useState([]);
const [catalogOwnershipTypes, setCatalogOwnershipTypes] = useState([]);
const [catalogStatuses,       setCatalogStatuses]       = useState([]);
const [svlApprovers,          setSvlApprovers]          = useState([]); 
  const {
    filteredAssets,
    searchTerm,           setSearchTerm,
    activeFilter,         setActiveFilter,
    selectedCategory,     setSelectedCategory,
    selectedSubCategory,  setSelectedSubCategory,
    selectedLocation,     setSelectedLocation,
    serializationFilter,  setSerializationFilter,
    warrantyExpiryFilter, setWarrantyExpiryFilter,
    olderThanValue,       setOlderThanValue,
    olderThanUnit,        setOlderThanUnit,
    showFilters,          setShowFilters,
    resetFilters,
    assetTypeFilter,      setAssetTypeFilter,
    ownershipTypeFilter,  setOwnershipTypeFilter,
    sortField,            setSortField,
    approvalStatusFilter, setApprovalStatusFilter, 
    taggedFilter,         setTaggedFilter,
    sortDirection,        setSortDirection,
  } = useAssetFilters(enrichedAssets);

  const { defaultPageSize, prefLoading } = useAssetsPageSize(currentUser);
useEffect(() => {
  if (!prefLoading) {
    const resolved = defaultPageSize === -1 
      ? -1 
      : (Number(defaultPageSize) || DEFAULT_PAGE_SIZE);
    setRowsPerPage(resolved);
    setPage(0);
  }
}, [prefLoading, defaultPageSize]);
  /* ── COLUMN PREFERENCES ── */
  const {
    visibleColumns,
    toggleColumn,
    showAllColumns,
    hideAllOptionalColumns,
    resetColumns,
  } = useColumnPreferences();

  const [showColumnPrefsModal, setShowColumnPrefsModal] = useState(false);

  const visibleDataColumns = useMemo(
    () => DATA_COLUMNS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  const tableMinWidth = useMemo(
    () => Math.max(700, visibleDataColumns.length * 120),
    [visibleDataColumns]
  );

  const [pageState, setPageState] = useState("loading");

  const [showCreateModal,          setShowCreateModal]          = useState(false);
  const [showEditModal,            setShowEditModal]            = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal,          setShowDeleteModal]          = useState(false);
  const [showQRModal,              setShowQRModal]              = useState(false);
  const [showExtendWarrantyModal,  setShowExtendWarrantyModal]  = useState(false);
  const [showDisposeModal,         setShowDisposeModal]         = useState(false);
  const [showQRPrintModal,         setShowQRPrintModal]         = useState(false);
  const [showQuantityModal,        setShowQuantityModal]        = useState(false);
  const [showReturnModal,          setShowReturnModal]          = useState(false);
  const [showReassignModal,        setShowReassignModal]        = useState(false);
  const [showStockModal,           setShowStockModal]           = useState(false);
  const [showApprovalHistoryModal, setShowApprovalHistoryModal] = useState(false);
  const [selectedApprovalAsset,    setSelectedApprovalAsset]    = useState(null);
  const [selectedAsset,         setSelectedAsset]         = useState(null);
  const [selectedQRAsset,       setSelectedQRAsset]       = useState(null);
  const [selectedWarrantyAsset, setSelectedWarrantyAsset] = useState(null);
  const [selectedDisposeAsset,  setSelectedDisposeAsset]  = useState(null);
  const [selectedQuantityAsset, setSelectedQuantityAsset] = useState(null);
  const [selectedReturnAsset,   setSelectedReturnAsset]   = useState(null);
  const [selectedReassignAsset, setSelectedReassignAsset] = useState(null);
  const [selectedStockAsset,    setSelectedStockAsset]    = useState(null);
  const [showTicketModal,   setShowTicketModal]   = useState(false);
  const [ticketTargetAsset, setTicketTargetAsset] = useState(null);
const [activeActionRowId, setActiveActionRowId] = useState(null);
const [hoveredRowId, setHoveredRowId] = useState(null);
  const [disposeModalInitialTab, setDisposeModalInitialTab] = useState('dispose');

  const [selectedAssets, setSelectedAssets] = useState(new Set());

  const [anchorEl,        setAnchorEl]        = useState(null);
  const [menuTargetAsset, setMenuTargetAsset] = useState(null);
  const [page,            setPage]            = useState(0);
 const [rowsPerPage,     setRowsPerPage]     = useState(() => {
 if (prefLoading) return DEFAULT_PAGE_SIZE;
  return defaultPageSize === -1 ? -1 : (Number(defaultPageSize) || 50);
});

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen                       = Boolean(exportAnchorEl);
  const handleExportMenuOpen                 = (e) => setExportAnchorEl(e.currentTarget);
  const handleExportMenuClose                = ()  => setExportAnchorEl(null);

  const [assetsForPrint, setAssetsForPrint] = useState([]);


  const subCategoryMap = useMemo(
    () => new Map(masterSubCategories.map((sc) => [sc.id, sc])),
    [masterSubCategories]
  );

  const availableLocations = useMemo(() => {
    // Build { code, name } list from master locations so we always have
    // the code to compare against asset.locationCode.
    const byCode = new Map();

    masterLocations.forEach((loc) => {
      const code = String(loc.locationCode || "").trim();
      if (!code) return;
      byCode.set(code, {
        code,
        name: String(loc.locationName || loc.name || code).trim(),
      });
    });

    // Also include any locationCode actually referenced by an asset, even
    // if it's missing from master data — otherwise those assets become
    // unfilterable.
    enrichedAssets.forEach((asset) => {
      const code = String(asset.locationCode || "").trim();
      if (!code || byCode.has(code)) return;
      byCode.set(code, { code, name: code });
    });

    return Array.from(byCode.values()).sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
  }, [enrichedAssets, masterLocations]);

  const hasActiveFilters = useMemo(
    () =>
      searchTerm.trim()    !== ""    ||
      activeFilter         !== "all" ||
      selectedCategory     !== "all" ||
      selectedSubCategory  !== "all" ||
      selectedLocation     !== "all" ||
      serializationFilter  !== "all" ||
      warrantyExpiryFilter !== "all" ||
      olderThanValue       !== ""    ||
      assetTypeFilter      !== "all" ||
     ownershipTypeFilter  !== "all" ||
     approvalStatusFilter !== "all",
     taggedFilter         !== "all",
  [
    searchTerm, activeFilter, selectedCategory, selectedSubCategory,
    selectedLocation, serializationFilter, warrantyExpiryFilter,
    olderThanValue, assetTypeFilter, ownershipTypeFilter,
    approvalStatusFilter,
  ]
);

  const activeFilterCount = [
    activeFilter         !== "all",
    serializationFilter  !== "all",
    selectedCategory     !== "all",
    selectedSubCategory  !== "all",
    selectedLocation     !== "all",
    warrantyExpiryFilter !== "all",
    olderThanValue       !== "",
    assetTypeFilter      !== "all",
    ownershipTypeFilter  !== "all",
   approvalStatusFilter !== "all",
   taggedFilter         !== "all",
].filter(Boolean).length;

  const filterKey = useMemo(
    () =>
      [
        searchTerm, activeFilter, selectedCategory, selectedSubCategory,
        selectedLocation, serializationFilter, warrantyExpiryFilter,
        olderThanValue, olderThanUnit, assetTypeFilter, ownershipTypeFilter,approvalStatusFilter,  taggedFilter, 
        sortField, sortDirection,
      ].join("|"),
    [
      searchTerm, activeFilter, selectedCategory, selectedSubCategory,
      selectedLocation, serializationFilter, warrantyExpiryFilter,
      olderThanValue, olderThanUnit, assetTypeFilter, ownershipTypeFilter,approvalStatusFilter,  taggedFilter,
      sortField, sortDirection,
    ]
  );

  useEffect(() => { 
  if (!prefLoading) {
    setPage(0); 
  }
}, [filterKey, prefLoading]);

  const handleClearAll = useCallback(() => {
    resetFilters();
    setSelectedAssets(new Set());
  }, [resetFilters]);

 const handleGoToPreferences = useCallback(
  () => navigate("/admin/settings", { state: { tab: 1 } }),
  [navigate]
);
  const fetchAssetsRef        = useRef(fetchAssets);
  const fetchAllMasterDataRef = useRef(fetchAllMasterData);
  useEffect(() => { fetchAssetsRef.current        = fetchAssets;        }, [fetchAssets]);
  useEffect(() => { fetchAllMasterDataRef.current = fetchAllMasterData; }, [fetchAllMasterData]);

  useEffect(() => {
    if (authLoading) return;

    if (!currentUser) {
      setPermissionError("Authentication required. Please log in.");
      setPageState("error");
      return;
    }

    const allowed =
      currentUser.role === "superadmin" ||
      currentUser.role === "admin"      ||
      (currentUser.role === "locationAdmin" && currentUser.companyId);

    if (!allowed) {
      setPermissionError("Access Denied: Administrator privileges required.");
      setPageState("error");
      return;
    }

    const MIN_MS = 400;
    const start  = Date.now();

    Promise.all([
      fetchAssetsRef.current(),
      fetchAllMasterDataRef.current(),
    ])
      .catch((err) => {
        setPermissionError(err?.message || "Unexpected error during load.");
        setPageState("error");
      })
      .finally(() => {
        const elapsed   = Date.now() - start;
        const remaining = MIN_MS - elapsed;
        const markReady = () => setPageState("ready");
        remaining > 0 ? setTimeout(markReady, remaining) : markReady();
      });
  }, [currentUser, authLoading, setPermissionError]);
useEffect(() => {
  if (!currentUser?.companyId) return;
  let cancelled = false;
  (async () => {
    try {
      const [enabled, emails, assetTypes, ownershipTypes, statuses, approvers] =
        await Promise.all([
          preferencesService.isOwnershipEnabled(currentUser),
          singleValueListService.getAssetOwnerBypassList(currentUser.companyId),
          singleValueListService.getItems("asset_types",     { companyId: currentUser.companyId }),
          singleValueListService.getItems("ownership_types", { companyId: currentUser.companyId }),
          singleValueListService.getItems("asset_statuses",  { companyId: currentUser.companyId }),
          singleValueListService.getApproverItems(currentUser.companyId),   // ← NEW
        ]);
      if (!cancelled) {
        setOwnershipOn(enabled);
        setBypassEmails(emails);
        setCatalogAssetTypes(assetTypes || []);
        setCatalogOwnershipTypes(ownershipTypes || []);
        setCatalogStatuses(statuses || []);
        setSvlApprovers(approvers || []);   // ← NEW
      }
    } catch (err) {
      console.warn("Ownership/bypass/catalog load failed:", err);
    }
  })();
  return () => { cancelled = true; };
}, [currentUser, prefsVersion]);
  const isSearchActive = searchTerm.trim() !== "";

const currentPageAssets = useMemo(() => {
    // Don't slice while preferences are still loading, otherwise it defaults to 50
    if (prefLoading) return []; 
    
    if (isSearchActive || rowsPerPage === -1) return filteredAssets;
    const start = page * rowsPerPage;
    return filteredAssets.slice(start, start + rowsPerPage);
  }, [filteredAssets, page, rowsPerPage, isSearchActive, prefLoading]);

  const handleAssetSelection = useCallback((assetId, isSelected) => {
    setSelectedAssets((prev) => {
      const next = new Set(prev);
      isSelected ? next.add(assetId) : next.delete(assetId);
      return next;
    });
  }, []);

  const toggleAllAssetsSelection = useCallback(
    (selectAll) => {
      const ids = currentPageAssets.map((a) => a.id);
      setSelectedAssets((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => (selectAll ? next.add(id) : next.delete(id)));
        return next;
      });
    },
    [currentPageAssets]
  );

  const showQRPrintModalHandler = useCallback(() => {
    if (selectedAssets.size === 0) {
      showNotification("Please select at least one asset to print QR codes.", "warning");
      return;
    }
    setAssetsForPrint(enrichedAssets.filter((a) => selectedAssets.has(a.id)));
    setShowQRPrintModal(true);
  }, [selectedAssets, enrichedAssets, showNotification]);

  const closeQRPrintModal = useCallback(() => {
    setShowQRPrintModal(false);
    setSelectedAssets(new Set());
    setAssetsForPrint([]);
  }, []);

  const handleViewInvoice = useCallback(
    (invoiceFileUrl) => {
      if (!invoiceFileUrl) {
        showNotification("Invoice file not found.", "error");
        return;
      }
      try {
        if (invoiceFileUrl.startsWith("data:")) {
          const commaIndex = invoiceFileUrl.indexOf(",");
          if (commaIndex === -1) throw new Error("Invalid base64 format.");
          const base64Data = invoiceFileUrl.substring(commaIndex + 1);
          const mimeMatch  = invoiceFileUrl.substring(0, commaIndex).match(/data:([^;]+)/);
          const mimeType   = mimeMatch ? mimeMatch[1] : "application/octet-stream";
          const byteChars  = atob(base64Data);
          const byteNums   = new Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++)
            byteNums[i] = byteChars.charCodeAt(i);
          const blob = new Blob([new Uint8Array(byteNums)], { type: mimeType });
          const url  = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 6000);
        } else if (
          invoiceFileUrl.startsWith("http://") ||
          invoiceFileUrl.startsWith("https://")
        ) {
          window.open(invoiceFileUrl, "_blank");
        } else {
          throw new Error("Invalid file URL.");
        }
      } catch (error) {
        showNotification(`Failed to open invoice: ${error.message}`, "error");
      }
    },
    [showNotification]
  );

  //  export the currently sorted + filtered list from the UI
  const fetchExportData = useCallback(async () => {
    if (filteredAssets && filteredAssets.length > 0) {
      return filteredAssets; // These are already enriched
    }

    showNotification("Fetching all assets for export…", "info");
    try {
      const rawAssets = await assetService.fetchAssets({});

      // Hydrate the raw list with dynamic master data names
      const catMap = new Map(
        masterCategories.map((c) => [c.id || c.categoryId, c.name || c.categoryName])
      );
      const subCatMap = new Map(
        masterSubCategories.map((sc) => [sc.id || sc.subCategoryId, sc.name || sc.subCategoryName])
      );
      const brndMap = new Map(
        masterBrands.map((b) => [b.id || b.brandId, b.name || b.brandName])
      );

      return (rawAssets || []).map((a) => ({
        ...a,
        categoryName:    catMap.get(a.categoryId)       || a.categoryName    || "",
        subCategoryName: subCatMap.get(a.subCategoryId) || a.subCategoryName || "",
        brandName:       brndMap.get(a.brandId)         || a.brandName       || "",
      }));
    } catch {
      showNotification("Failed to fetch all assets for export.", "error");
      return null;
    }
  }, [filteredAssets, showNotification, masterCategories, masterSubCategories, masterBrands]);

  const buildAndDownloadCSV = useCallback(
    (dataToExport) => {
      // ── Sanitize any non-ASCII characters that Excel mangles ──────────
      const sanitizeForCsv = (str) => {
        if (str == null) return "";
        return String(str)
          .replace(/\u2014/g, "-")         
          .replace(/\u2013/g, "-")         
          .replace(/\u2018|\u2019/g, "'")   // smart single quotes
          .replace(/\u201C|\u201D/g, '"')   // smart double quotes
          .replace(/\u2026/g, "...")        // ellipsis
          .replace(/\u00A0/g, " ")          // non-breaking space
          .replace(/\u20B9/g, "INR ")       // ₹
          .replace(/\u00AE/g, "(R)")
          .replace(/\u2122/g, "(TM)")
          .replace(/\u00A9/g, "(C)");
      };

      const exportData = dataToExport.map((asset) => {
        const dep = getAssetDepreciation(asset, subCategoryMap);

        const ownership = String(asset.ownershipType || "").toLowerCase();
const isLeased  = ownership === "leased";
const isRented  = ownership === "rented";
const isOwned   = !isLeased && !isRented;

        return {
          "Asset ID":              asset.assetId                   || "",
          "Asset Name":            asset.assetName                 || "",
          "Asset Description":     asset.assetDescription          || "",
          "Company ID":            asset.companyId                 || "",
          // 2. CLASSIFICATION
          "Asset Type":            asset.assetType                 || "Primary Asset",
          "Ownership Type":        asset.ownershipType             || "Owned",
          // 3. PARENT-CHILD
          "Parent Asset Name":     asset.parentAssetName           || "",
          "Purchased Separately":  asset.purchasedSeparately ? "Yes" : "No",
          // 4. CATEGORIZATION
          "Category Name":         asset.categoryName              || "",
          "Sub-Category Name":     asset.subCategoryName           || "",
          "Brand Name":            asset.brandName                 || "",
          // 5. TRACKING
          "Serialization Type":    asset.serializationType         || SERIALIZATION_TYPES.SERIALIZED,
          "Manufacturer Serial Number": asset.manufacturerSerialNumber || "",
         
          "Quantity":              asset.quantity                  || 1,
          "Unit of Measurement":   asset.unitOfMeasurement         || "",
         
          "Expiry Date":           formatDate(asset.expiryDate),
                "Status":                asset.status                    || "",
                   "Approval Status":       getApprovalStatusForAsset(asset),
          // 6. ASSIGNMENT
          "Assigned To":           asset.assignedToName                || "",
          "Assigned On":           formatDate(asset.assignedOn),
          
          // 7. OWNERSHIP
          "Owner Email":           asset.ownerEmail                || "",
          "Owner Assigned On":     formatDate(asset.ownerAssignedOn),
          
          // 8. LOCATION
          "Location Code":         asset.locationCode              || "",
         
          // 9. OWNED
          "Purchase Date":         isOwned ? formatDate(asset.purchaseDate || asset.purchasedDate) : "",
          "Purchase Value (INR)":  isOwned ? (asset.purchaseValue || 0) : "",
          "Vendor Name":           isOwned ? (asset.vendorName || "") : "",
          "Warranty Expiry":       isOwned ? formatDate(asset.warrantyExpiry) : "",
         
          "Invoice Number":        isOwned ? (asset.invoiceNumber || "") : "",
          // 10. LEASED
          "Lease Start Date":       isLeased ? formatDate(asset.leaseStartDate) : "",
          "Lease End Date":         isLeased ? formatDate(asset.leaseEndDate) : "",
          "Lease Amount (INR)":     isLeased ? (asset.leaseAmount || "") : "",
          
          "Lease Vendor Name":      isLeased ? (asset.leaseVendorName || "") : "",
          "Lease Agreement Number": isLeased ? (asset.leaseAgreementNumber || "") : "",
          "Lease Billing Frequency": isLeased ? (asset.leaseBillingFrequency || "") : "",
          // 11. RENTED
          "Rent Start Date":        isRented ? formatDate(asset.rentStartDate) : "",
          "Expected Return Date":   isRented ? formatDate(asset.expectedReturnDate) : "",
          "Rent Amount (INR)":      isRented ? (asset.rentAmount || "") : "",
         
          "Rent Vendor Name":       isRented ? (asset.rentVendorName || "") : "",
          "Rent Agreement Number":  isRented ? (asset.rentAgreementNumber || "") : "",
          "Rent Billing Frequency": isRented ? (asset.rentBillingFrequency || "") : "",
          // 12. DEPRECIATION
          "Depreciation Method":     getDepreciationLabel(dep.method),
          "Useful Life (Years)":     dep.usefulLife                  || "",
          "Depreciation Rate (%)":   dep.depreciationRate            || "",
          "Residual Value (INR)":    dep.residualValue               || 0,

         
         
          // 13. MAINTENANCE
          "Maintenance Frequency":  asset.maintenanceFrequency      || "",
          "Maintenance Start Date": formatDate(asset.maintenanceStartDate),
          // 15. RETURN
          "Return Date":            formatDate(asset.returnDate),
          "Return Remarks":         asset.returnRemarks             || "",
         
          // 16. DISPOSAL
          "Disposal Date":          formatDate(asset.disposalDate),
          "Disposal Reason":        asset.disposalReason            || "",
          "Disposal Remarks":       asset.disposalRemarks           || "",
         
        };
      });

      const headers = Object.keys(exportData[0] || {});

      const csvContent = [
        headers.join(","),
        ...exportData.map((row) =>
          headers
            .map((h) => {
              const val = sanitizeForCsv(row[h]);
              return val.includes(",") ||
                     val.includes('"') ||
                     val.includes("\n") ||
                     val.includes("\r")
                ? `"${val.replace(/"/g, '""')}"`
                : val;
            })
            .join(",")
        ),
      ].join("\r\n");   

     
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      
      const link = document.createElement("a");
      link.href  = URL.createObjectURL(blob);
      link.setAttribute(
        "download",
        `Asset_Export_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      },
    [subCategoryMap, svlApprovers]
  );
  const handleExportAssets = useCallback(async () => {
    const data = await fetchExportData();
    if (!data) return;
    if (!data.length) { showNotification("No assets to export.", "warning"); return; }
    try {
      buildAndDownloadCSV(data);
      showNotification(`Exported successfully.`, "success");
    } catch {
      showNotification("Export failed. Please try again.", "error");
    }
  }, [fetchExportData, buildAndDownloadCSV, showNotification]);
    const exportToPDF = useCallback(async () => {
    const data = await fetchExportData();
    if (!data?.length) {
      showNotification("No assets to export.", "warning");
      return;
    }

    try {
      const { jsPDF, autoTable } = await getPDFLibraries();

      const doc = new jsPDF("landscape");

      doc.setFontSize(16);
      doc.text("Asset Management Report", 14, 15);

      const tableOptions = {
        startY: 24,
        head: [[
          "Asset ID", "Asset Name", "Asset Type", "Ownership", "Parent Asset",
          "Category", "Tracking", "Serial Number", "Quantity",
          "Purchase Date", "Purchase Value (INR)", "Status", "Location", "Depreciation",
        ]],
        body: data.map((a) => {
          const dep = getAssetDepreciation(a, subCategoryMap);
          return [
            a.assetId      || "",
            a.assetName    || "",
            a.assetType    || "Primary Asset",
            a.ownershipType || "Owned",
            a.parentAssetName || "—",
            a.categoryName || "",
            String(a.serializationType || "").toLowerCase() === "non-serialized" ? "Bulk" : "Serialized",
            a.manufacturerSerialNumber || "—",
            (a.quantity || 1).toString(),
            formatDate(a.purchaseDate || a.purchasedDate),
            a.purchaseValue ? formatCurrency(a.purchaseValue) : "—",
                        a.status || "",
                        getApprovalStatusForAsset(a),
            a.locationName || a.currentLocationName || "",
            getDepreciationLabel(dep.method),
          ];
        }),
        theme:      "grid",
        headStyles: { fillColor: [21, 101, 192], fontSize: 8 },
        styles:     { fontSize: 7 },
      };

      // Call autoTable directly as a standalone function passing doc
      if (typeof autoTable === "function") {
        autoTable(doc, tableOptions);
      } else if (typeof doc.autoTable === "function") {
        doc.autoTable(tableOptions);
      } else {
        throw new Error("Unable to execute autoTable generator.");
      }

      doc.save(`Asset_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      showNotification(`PDF exported with ${data.length} asset(s).`, "success");
    } catch (err) {
      console.error("[PDF Export] Failed:", err);
      showNotification(`Failed to generate PDF: ${err.message}`, "error");
    }
   }, [fetchExportData, subCategoryMap, svlApprovers, showNotification]);

  const handleAssetCreated = useCallback(() => {
    fetchAssets();
    setShowCreateModal(false);
    showNotification("Asset created successfully.");
  }, [fetchAssets, showNotification]);

  const handleAssetUpdated = useCallback(
    async (updatedAsset) => {
      try {
        if (!currentUser?.email) return;
        await logAuditEvent({
          actionType:  "update",
          entityType:  "Asset",
          entityId:    updatedAsset.id,
          description: `Asset updated: ${updatedAsset.assetName}`,
          companyId:   updatedAsset.companyId,
          userId:      currentUser?.uid,
        });
        showNotification("Asset updated successfully.");
        fetchAssets();
      } catch (error) {
        showNotification(error.message, "error");
      } finally {
        setShowEditModal(false);
        setSelectedAsset(null);
      }
    },
    [fetchAssets, currentUser, showNotification]
  );

  const handleDeleteConfirm = useCallback(async () => {
    try {
      if (selectedAsset) {
        //  deleteAsset(id)
        await assetService.deleteAsset(selectedAsset.id);
        await logAuditEvent({
          actionType:  "delete",
          entityType:  "Asset",
          entityId:    selectedAsset.id,
          description: `Asset soft-deleted: ${selectedAsset.assetName}`,
          companyId:   selectedAsset.companyId,
          userId:      currentUser?.uid,
        });
        showNotification("Asset deleted successfully.");
        fetchAssets();
      }
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      setShowDeleteModal(false);
      setSelectedAsset(null);
    }
  }, [selectedAsset, fetchAssets, currentUser, showNotification]);

 const handleWarrantyExtended = useCallback(
  async (result) => {
    try {
      
      const assetId = result?.assetId ?? selectedWarrantyAsset?.id;
      const assetName = selectedWarrantyAsset?.assetName || "";

      try {
        await logAuditEvent({
          actionType:  "update",
          entityType:  "Asset",
          entityId:    assetId,
          description: `Warranty extended: ${assetName}`,
          companyId:   selectedWarrantyAsset?.companyId,
          userId:      currentUser?.uid,
        });
      } catch (auditErr) {
        console.warn("Audit log failed for warranty extension:", auditErr);
      }

      showNotification("Warranty extended successfully.", "success");
      fetchAssets();
    } catch (error) {
      showNotification(error.message || "Failed to extend warranty.", "error");
    } finally {
      setShowExtendWarrantyModal(false);
      setSelectedWarrantyAsset(null);
    }
  },
  [selectedWarrantyAsset, fetchAssets, currentUser, showNotification]
);

  /**
   *  Called by DisposeAssetModal after a successful disposal.
   *
   * The modal itself calls assetService.disposeAsset() — this handler only
   * handles post-success side effects (audit log, refresh, toast, close).
   */
  const handleAssetDisposed = useCallback(
    async (updatedAsset) => {
      try {
        // Best-effort audit log — never block the success path on failure.
        try {
          await logAuditEvent({
            actionType:  "dispose",
            entityType:  "Asset",
            entityId:    updatedAsset?.id || selectedDisposeAsset?.id,
            description: `Asset disposed: ${updatedAsset?.assetName || selectedDisposeAsset?.assetName || ""}`,
            companyId:   updatedAsset?.companyId || selectedDisposeAsset?.companyId,
            userId:      currentUser?.uid,
          });
        } catch (auditErr) {
          console.warn("Audit log failed for disposal:", auditErr);
        }

        showNotification("Asset disposal recorded successfully.", "success");
        fetchAssets();
      } catch (error) {
        showNotification(error?.message || "Failed to process disposal.", "error");
      } finally {
        setShowDisposeModal(false);
        setSelectedDisposeAsset(null);
        setDisposeModalInitialTab('dispose');
      }
    },
    [selectedDisposeAsset, fetchAssets, currentUser, showNotification]
  );

  /**
   * Called by ReturnAssetModal after a successful return.
   *
   * The modal itself calls assetService.returnAsset() — this handler only
   * handles post-success side effects (audit log, refresh, toast, close).
   */
  const handleAssetReturned = useCallback(
    async (updatedAsset) => {
      try {
        // Best-effort audit log — never block the success path on failure.
        try {
          await logAuditEvent({
            actionType:  "return",
            entityType:  "Asset",
            entityId:    updatedAsset?.id || selectedReturnAsset?.id,
            description: `Asset returned: ${updatedAsset?.assetName || selectedReturnAsset?.assetName || ""}`,
            companyId:   updatedAsset?.companyId || selectedReturnAsset?.companyId,
            userId:      currentUser?.uid,
          });
        } catch (auditErr) {
          console.warn("Audit log failed for return:", auditErr);
        }

        showNotification("Asset returned successfully.", "success");
        fetchAssets();
      } catch (error) {
        showNotification(error?.message || "Failed to process return.", "error");
      } finally {
        setShowReturnModal(false);
        setSelectedReturnAsset(null);
      }
    },
    [selectedReturnAsset, fetchAssets, currentUser, showNotification]
  );

  /**
   * Reassign / Release handler.
      */
  const handleAssetReassigned = useCallback(
    async (updatedAsset) => {
      try {
        const asset = selectedReassignAsset;
        if (!asset) return;

        const wasAssigned  = Boolean(asset.assignedTo);
        const willBeAssigned =
  Boolean(updatedAsset?.assignedTo) &&
  String(updatedAsset?.status || "").toLowerCase() !== "available";
        let actionLabel = "updated";
        let payload;

        if (!willBeAssigned && wasAssigned) {
          // Release
          payload = buildReleasePayload({
            asset,
            notes: updatedAsset?.notes || "",
            currentUser,
          });
          actionLabel = "released";
        } else if (willBeAssigned) {
          const built = buildAssignPayload({
            asset,
            assignedTo:             updatedAsset?.assignedTo             ?? "",
            assignedToName:         updatedAsset?.assignedToName         ?? "",
            assignedDepartmentId:   updatedAsset?.assignedDepartmentId   ?? "",
            assignedDepartmentName: updatedAsset?.assignedDepartmentName ?? "",
            notes:                  updatedAsset?.notes                  || "",
            expectedReturnDate:     updatedAsset?.expectedReturnDate     || null,
            currentUser: {
              uid:         currentUser?.uid         || "",
              email:       currentUser?.email       || "",
              displayName: currentUser?.displayName || currentUser?.email || "",
            },
          });
          payload = built.payload;
          actionLabel = built.isReassignment ? "reassigned" : "assigned";
        } else {
          return;
        }

        await assetService.upsertData(asset.id, payload);

        try {
          await logAuditEvent({
            actionType:  actionLabel,
            entityType:  "Asset",
            entityId:    asset.id,
            description:
              actionLabel === "released"
                ? `Asset released: ${asset.assetName} — from "${asset.assignedToName || asset.assignedTo || "Unassigned"}"`
                : `Asset ${actionLabel}: ${asset.assetName} — to "${updatedAsset?.assignedToName || updatedAsset?.assignedTo || "Unassigned"}"`,
            companyId:   asset.companyId,
            userId:      currentUser?.uid,
          });
        } catch (auditErr) {
          console.warn("Audit log failed for reassignment:", auditErr);
        }
      } catch (error) {
        showNotification(error.message || "Failed to update assignment.", "error");
      } finally {
        fetchAssets();
        setShowReassignModal(false);
        setSelectedReassignAsset(null);
      }
    },
    [selectedReassignAsset, fetchAssets, currentUser, showNotification]
  );

  const updateAssetQuantity = useCallback(
    async (assetId, newQuantity, adjustmentData) => {
      try {
        const currentAsset = enrichedAssets.find((a) => a.id === assetId);
        const now = new Date().toISOString();

        await assetService.upsertData(assetId, {
          ...currentAsset,
          quantity: Number(newQuantity) || 0,
          quantityAdjustments: [
            ...(Array.isArray(currentAsset?.quantityAdjustments)
              ? currentAsset.quantityAdjustments
              : []),
            {
              ...(adjustmentData || {}),
              adjustedAt: now,
              adjustedBy: currentUser?.email || "system",
              previousQuantity: Number(currentAsset?.quantity) || 0,
              newQuantity:      Number(newQuantity) || 0,
            },
          ],
          updatedAt: now,
          updatedBy: currentUser?.email || "system",
        });

        showNotification("Asset quantity adjusted successfully.");
        await fetchAssets();
      } catch (error) {
        showNotification(error.message || "Failed to adjust quantity.", "error");
      } finally {
        setShowQuantityModal(false);
        setSelectedQuantityAsset(null);
      }
    },
    [currentUser, enrichedAssets, fetchAssets, showNotification]
  );

  const handleSortClick = (columnKey) => {
    if (columnKey === "actions") return;
    const isAsc = sortField === columnKey && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(columnKey);
  };
const canEditAssetRow = useCallback((asset) => {
  if (!asset) return false;

  // Rule #1 — approval gate.
  // Draft / Returned / Approved (any variant) / NA → editable.
  // Pending / Rejected / Correction → view only.
  if (isApprovalLocked(asset)) return false;

  // Rule #2 — ownership lock (unchanged)
  if (!ownershipOn) return true;
  if (!asset?.ownerId && !asset?.ownerEmail) return true;

  const uid   = currentUser?.id || currentUser?.uid;
  const email = String(currentUser?.email || "").toLowerCase();
  const role  = String(currentUser?.role || "").toLowerCase();

  if (asset.ownerId && String(asset.ownerId) === String(uid)) return true;
  if (String(asset.ownerEmail || "").toLowerCase() === email) return true;
  if (bypassEmails.includes(email)) return true;
  if (role === "admin" || role === "superadmin") return true;

  return false;
}, [ownershipOn, bypassEmails, currentUser]);

const canDeleteAssetRow = useCallback((asset) => {
  if (!asset) return false;


  if (!isApprovalLocked(asset)) return true;

  const role = String(currentUser?.role || "").toLowerCase();
  return role === "admin" || role === "superadmin";
}, [currentUser]);

  const handleEditClick = useCallback((asset) => {
  setPrefsVersion((v) => v + 1);
  setSelectedAsset(asset);
  setShowEditModal(true);
}, []);

  const handleQRClick = useCallback(
    (asset) => { setSelectedQRAsset(asset);       setShowQRModal(true);             }, []
  );
  const handleExtendWarrantyClick = useCallback(
    (asset) => { setSelectedWarrantyAsset(asset); setShowExtendWarrantyModal(true); }, []
  );

  // handleDisposeClick — opens dispose tab
  const handleDisposeClick = useCallback(
    (asset) => {
      setSelectedDisposeAsset(asset);
      setDisposeModalInitialTab('dispose');
      setShowDisposeModal(true);
    }, []
  );

  //  handleDisposalHistoryClick — opens history tab in same modal
  const handleDisposalHistoryClick = useCallback(
    (asset) => {
      setSelectedDisposeAsset(asset);
      setDisposeModalInitialTab('history');
      setShowDisposeModal(true);
    }, []
  );

  const handleDeleteClick = useCallback(
    (asset) => { setSelectedAsset(asset);         setShowDeleteModal(true);         }, []
  );
  const handleQuantityAdjustClickLocal = useCallback(
    (asset) => { setSelectedQuantityAsset(asset); setShowQuantityModal(true);       }, []
  );
  const handleReturnClick = useCallback(
    (asset) => { setSelectedReturnAsset(asset);   setShowReturnModal(true);         }, []
  );
  const handleReassignClick = useCallback(
    (asset) => { setSelectedReassignAsset(asset); setShowReassignModal(true);       }, []
  );
    /* ── HelpDesk handlers ──────────────────────────────────────────────── */
  const handleRaiseTicketClick = useCallback((asset) => {
    setTicketTargetAsset(asset);
    setShowTicketModal(true);
  }, []);

  const handleTicketClose = useCallback(() => {
    setShowTicketModal(false);
    setTicketTargetAsset(null);
  }, []);

  const handleTicketSuccess = useCallback(
    (msg) => {
      showNotification(msg || "Ticket created successfully.", "success");
      fetchAssets();        
      handleTicketClose();
    },
    [showNotification, fetchAssets, handleTicketClose]
  );
    const handleApprovalHistoryClick = useCallback((asset) => {
    setSelectedApprovalAsset(asset);
    setShowApprovalHistoryModal(true);
  }, []);
  const handleStockTransactionClick = useCallback(
    (asset, defaultType = "Receipt") => {
      setSelectedStockAsset({ ...asset, _defaultTransactionType: defaultType });
      setShowStockModal(true);
    },
    []
  );
  const handleRecordConsumptionClick = useCallback(
    (asset) => {
      navigate("/admin/consumptions", {
        state: { preSelectedItem: asset, preSelectedItemType: asset.assetType },
      });
    },
    [navigate]
  );

  const handleMenuOpen = useCallback(
    (event, asset) => { setAnchorEl(event.currentTarget); setMenuTargetAsset(asset); },
    []
  );
  const handleMenuClose = useCallback(
    () => { setAnchorEl(null); setMenuTargetAsset(null); },
    []
  );
  const handleChangePage = useCallback((_, newPage) => setPage(newPage), []);

  if (pageState === "error") {
    return <Box sx={{ p: 4 }}><PermissionDenied message={permissionError} /></Box>;
  }
  if (pageState === "loading") return <AssetManagementSkeleton />;

  const allCurrentPageSelected =
    currentPageAssets.length > 0 &&
    currentPageAssets.every((a) => selectedAssets.has(a.id));

  const someCurrentPageSelected =
    currentPageAssets.some((a) => selectedAssets.has(a.id)) &&
    !allCurrentPageSelected;

  const pageSizeLabel = rowsPerPage === -1 ? "All" : String(rowsPerPage);

  const showPaginationNav =
    !isSearchActive &&
    rowsPerPage !== -1 &&
    filteredAssets.length > rowsPerPage;

    const menuTargetIsAssigned = isAssetAssigned(menuTargetAsset);

  const actionsAllowedByApproval = isScanAllowedByApproval(menuTargetAsset);

  const hiddenColumnCount = DATA_COLUMNS.length - visibleDataColumns.length;

  return (
    <Container
      maxWidth="xl"
      sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}
    >

      {masterDataError && (
        <Alert
          severity="warning" sx={{ borderRadius: 2 }}
          onClose={() => setMasterDataError("")}
        >
          {masterDataError} — Some features may be limited.
        </Alert>
      )}

      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1"
            sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.5px" }}>
            Asset Management Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage assets, track lifecycle, warranties, and depreciation
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => {
  setPrefsVersion((v) => v + 1);
  setShowCreateModal(true);
}}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 2.5 }}
            disableElevation
          >
            Create Asset
          </Button>

          <Button
            variant="outlined" startIcon={<FilterAltIcon />}
            onClick={() => setShowFilters((v) => !v)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, position: "relative" }}
          >
            {showFilters ? "Hide Filters" : "Filters"}
            {!showFilters && activeFilterCount > 0 && (
              <Box component="span" sx={{
                position: "absolute", top: -6, right: -6,
                bgcolor: "warning.main", color: "warning.contrastText",
                borderRadius: "50%", width: 18, height: 18,
                fontSize: "0.6rem", fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                lineHeight: 1, pointerEvents: "none",
              }}>
                {activeFilterCount}
              </Box>
            )}
          </Button>

          <Button
            variant="outlined" color="primary"
            startIcon={<FileDownloadIcon />} endIcon={<KeyboardArrowDownIcon />}
            onClick={handleExportMenuOpen}
            aria-controls={exportMenuOpen ? "export-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={exportMenuOpen ? "true" : undefined}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Export
          </Button>
{!loading && enrichedAssets.length === 0 && (
  <Button
    variant="outlined"
    color="primary"
    startIcon={<CloudUploadIcon />}
    onClick={() => setShowImportModal(true)}
    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
  >
    Import
  </Button>
)}
          <Menu
            id="export-menu" anchorEl={exportAnchorEl} open={exportMenuOpen}
            onClose={handleExportMenuClose} TransitionComponent={Fade}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { minWidth: 180, borderRadius: 2, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", mt: 0.5 } }}
          >
            <MenuItem
              onClick={() => { handleExportMenuClose(); handleExportAssets(); }}
              sx={{ gap: 1.5, py: 1 }}
            >
              <FileDownloadIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={500}>Export CSV</Typography>
            </MenuItem>
            <MenuItem
              onClick={() => { handleExportMenuClose(); exportToPDF(); }}
              sx={{ gap: 1.5, py: 1 }}
            >
              <PictureAsPdfIcon fontSize="small" color="error" />
              <Typography variant="body2" fontWeight={500}>Export PDF</Typography>
            </MenuItem>
                       <MenuItem
              onClick={async () => {
                handleExportMenuClose();
                try {
                  const blob =
                    await assetImportService.exportAsImportTemplate(currentUser);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `Niyantrix_Asset_Export_${new Date()
                    .toISOString()
                    .slice(0, 10)}.xlsx`;
                  a.click();
                  URL.revokeObjectURL(url);
                  showNotification("Exported as import template.", "success");
                } catch (err) {
                  showNotification(
                    err?.message || "Failed to export template.",
                    "error"
                  );
                }
              }}
              sx={{ gap: 1.5, py: 1 }}
            >
              <FileDownloadIcon fontSize="small" color="success" />
              <Typography variant="body2" fontWeight={500}>
                Export (Import Template)
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ══ SEARCH BAR ══════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 2, borderRadius: 3, bgcolor: "background.paper" }} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" variant="outlined"
              label="Search Assets"
              placeholder="Search by name, ID, serial, asset type, ownership..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "action.active" }} fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          {/* ── COLUMN PREFERENCES BUTTON (next to Search Assets) ── */}
          <Grid item xs={12} md="auto">
            <Tooltip title="Show / hide table columns" arrow>
              <Button
                variant="outlined" color="primary" size="medium"
                startIcon={<ViewColumnIcon />}
                onClick={() => setShowColumnPrefsModal(true)}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 600,
                  height: 40, whiteSpace: "nowrap",
                }}
              >
                Column Preferences
                <Chip
                  label={`${visibleDataColumns.length}/${DATA_COLUMNS.length}`}
                  size="small"
                  color={hiddenColumnCount > 0 ? "warning" : "default"}
                  sx={{ ml: 1, height: 20, fontSize: "0.68rem", fontWeight: 700 }}
                />
              </Button>
            </Tooltip>
          </Grid>

          {isSearchActive && (
            <Grid item xs={12} md>
              <Typography variant="body2" color="text.secondary">
                Found <strong>{filteredAssets.length}</strong> asset
                {filteredAssets.length !== 1 ? "s" : ""} matching "
                {searchTerm}" — showing all results
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ══ FILTER PANEL ════════════════════════════════════════════════════ */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper" }} variant="outlined">
          <Grid container spacing={2} alignItems="flex-start">

           <Grid item xs={12} sm={6} md={4} lg={2}>
  <FormControl fullWidth size="small">
    <InputLabel>Assignment</InputLabel>
    <Select value={activeFilter} label="Assignment"
      onChange={(e) => setActiveFilter(e.target.value)} sx={{ borderRadius: 2 }}>
      <MenuItem value="all">All</MenuItem>
      <MenuItem value="available">Available</MenuItem>
      <MenuItem value="assigned">Assigned / In Use</MenuItem>
      <MenuItem value="maintenance">In Maintenance</MenuItem>
      <MenuItem value="repair">Under Repair</MenuItem>
      <MenuItem value="reserved">Reserved</MenuItem>
      <MenuItem value="disposed">Disposed</MenuItem>
      <MenuItem value="lost">Lost / Stolen</MenuItem>
    </Select>
  </FormControl>
</Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Asset Type</InputLabel>
                <Select value={assetTypeFilter} label="Asset Type"
  onChange={(e) => setAssetTypeFilter(e.target.value)} sx={{ borderRadius: 2 }}>
  <MenuItem value="all">All Types</MenuItem>
  {catalogAssetTypes.map((t) => (
    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
  ))}
</Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ownership Type</InputLabel>
                <Select value={ownershipTypeFilter} label="Ownership Type"
  onChange={(e) => setOwnershipTypeFilter(e.target.value)} sx={{ borderRadius: 2 }}>
  <MenuItem value="all">All Ownership</MenuItem>
  {catalogOwnershipTypes.map((t) => (
    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
  ))}
</Select>
              </FormControl>
            </Grid>
<Grid item xs={12} sm={6} md={4} lg={2}>
  <FormControl fullWidth size="small">
    <InputLabel>Approval Status</InputLabel>
    <Select
      value={approvalStatusFilter}
      label="Approval Status"
      onChange={(e) => setApprovalStatusFilter(e.target.value)}
      sx={{ borderRadius: 2 }}
    >
      <MenuItem value="all">All Statuses</MenuItem>
      {(() => {
        // Start with the base system statuses so they are ALWAYS available in the dropdown
        const statuses = new Set(["NA", "Draft", "Returned", "Approved"]);
        
        // Add any custom SVL statuses (like "Reviewed") found in the current assets
      enrichedAssets.forEach((a) => {
          const label = getApprovalStatusForAsset(a);
          if (label) {
           
            const normalized = label.toLowerCase().includes("pending") ? "Draft" : label;
            statuses.add(normalized);
          }
        });

        return Array.from(statuses).sort().map((s) => (
          <MenuItem key={s} value={s}>
            {s === "NA" ? "NA (Not Applicable)" : s}
          </MenuItem>
        ));
           })()}
    </Select>
  </FormControl>
</Grid>

{/*  Tagged Filter */}
<Grid item xs={12} sm={6} md={4} lg={2}>
  <FormControl fullWidth size="small">
    <InputLabel>Tagged Status</InputLabel>
    <Select
      value={taggedFilter}
      label="Tagged Status"
      onChange={(e) => setTaggedFilter(e.target.value)}
      sx={{ borderRadius: 2 }}
    >
      <MenuItem value="all">All</MenuItem>
      <MenuItem value="yes">Tagged (Yes)</MenuItem>
      <MenuItem value="no">Untagged (No)</MenuItem>
    </Select>
  </FormControl>
</Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tracking Type</InputLabel>
                <Select value={serializationFilter} label="Tracking Type"
                  onChange={(e) => setSerializationFilter(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="serialized">Serialized</MenuItem>
                  <MenuItem value="non-serialized">Non-Serialized</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={selectedCategory} label="Category"
                  onChange={(e) => setSelectedCategory(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((cat, idx) => (
                    <MenuItem key={idx} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sub-Category</InputLabel>
                <Select value={selectedSubCategory} label="Sub-Category"
                  onChange={(e) => setSelectedSubCategory(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="all">All Sub-Categories</MenuItem>
                  {subCategories.map((sc, idx) => (
                    <MenuItem key={idx} value={sc}>{sc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 15 }} />
                    Location
                  </Box>
                </InputLabel>
                <Select
                  value={selectedLocation}
                  label="Location"
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  sx={{ borderRadius: 2 }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                                <MenuItem value="all">All Locations</MenuItem>
                  {availableLocations.length === 0 ? (
                    <MenuItem value="" disabled>
                      <Typography variant="caption" color="text.disabled">
                        No locations available
                      </Typography>
                    </MenuItem>
                  ) : (
                                      availableLocations.map((loc) => (
                      <MenuItem key={loc.code} value={loc.code}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <LocationOnIcon sx={{ fontSize: 14, color: "action.active" }} />
                          {loc.name}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Warranty Status</InputLabel>
                <Select value={warrantyExpiryFilter} label="Warranty Status"
                  onChange={(e) => setWarrantyExpiryFilter(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="expiring">Expiring (30 days)</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Box sx={{ display: "flex", alignItems: "stretch", gap: 0 }}>
                <TextField
                  size="small" label="Asset age" type="number"
                  placeholder="e.g. 5" value={olderThanValue}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") { setOlderThanValue(""); return; }
                    const parsed = parseInt(raw, 10);
                    if (!isNaN(parsed) && parsed > 0) setOlderThanValue(String(parsed));
                  }}
                  inputProps={{ min: 1, step: 1 }}
                  InputProps={{
                    endAdornment: olderThanValue !== "" && (
                      <InputAdornment position="end" sx={{ mr: -0.5 }}>
                        <IconButton size="small" onClick={() => setOlderThanValue("")} sx={{ p: 0.25 }}>
                          <ClearIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1, minWidth: 0,
                    "& .MuiOutlinedInput-root": { borderRadius: "8px 0 0 8px" },
                    "& .MuiOutlinedInput-notchedOutline": { borderRight: "none" },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                    "& input[type=number]": { MozAppearance: "textfield" },
                  }}
                />
                <FormControl size="small" sx={{ width: 90, flexShrink: 0 }}>
                  <Select
                    value={olderThanUnit}
                    onChange={(e) => setOlderThanUnit(e.target.value)}
                    sx={{ borderRadius: "0 8px 8px 0", "& .MuiOutlinedInput-notchedOutline": { borderLeftColor: "rgba(0,0,0,0.23)" } }}
                  >
                    <MenuItem value="years">Years</MenuItem>
                    <MenuItem value="months">Months</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {olderThanValue !== "" && Number(olderThanValue) > 0 && (
                <Typography variant="caption" color="primary.main" fontWeight={600}
                  display="block" sx={{ mt: 0.5, ml: 0.25 }}>
                  ≥ {olderThanValue}{" "}
                  {olderThanUnit === "years"
                    ? Number(olderThanValue) === 1 ? "year"  : "years"
                    : Number(olderThanValue) === 1 ? "month" : "months"}{" "}
                  old
                </Typography>
              )}
            </Grid>

                    {selectedLocation !== "all" && (
              <Grid item xs={12} sm={6} md={4} lg={3}
                sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.75,
                  px: 1.5, py: 0.5, borderRadius: 10,
                  bgcolor: "info.50", border: "1px solid",
                  borderColor: "info.200",
                }}>
                  <LocationOnIcon sx={{ fontSize: 14, color: "info.main" }} />
                                 <Typography variant="caption" color="info.main" fontWeight={600}>
                    Location:{" "}
                    {availableLocations.find((l) => l.code === selectedLocation)?.name ||
                      selectedLocation}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedLocation("all")}
                    sx={{ p: 0.15, ml: 0.25, color: "info.main" }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}><Divider sx={{ mt: 0.5, mb: 1.5 }} /></Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, px: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <QrCodeIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      Batch Print QR Codes
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedAssets.size === 0
                        ? "Select rows in the table using checkboxes, then print."
                        : `${selectedAssets.size} asset${selectedAssets.size !== 1 ? "s" : ""} selected`}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained" color="warning" size="small"
                  startIcon={<PrintIcon />}
                  onClick={showQRPrintModalHandler}
                  disabled={selectedAssets.size === 0}
                  disableElevation
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, minWidth: 160, "&.Mui-disabled": { opacity: 0.5 } }}
                >
                  {selectedAssets.size === 0
                    ? "Print QR Codes"
                    : `Print ${selectedAssets.size} QR Code${selectedAssets.size !== 1 ? "s" : ""}`}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mt: 0.5 }}>
                <Button
                  variant="text" color="info" onClick={handleClearAll}
                  startIcon={<ClearIcon fontSize="small" />}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                >
                  Clear Filters
                </Button>
                <Tooltip title="Close filters" arrow>
                  <IconButton size="small" onClick={() => setShowFilters(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

          </Grid>
        </Paper>
      </Collapse>

      {/* ══ TABLE ═══════════════════════════════════════════════════════════ */}
      <TableContainer
        component={Paper} variant="outlined"
        sx={{
          borderRadius: 3, boxShadow: "0px 4px 16px rgba(0,0,0,0.02)",
          maxHeight: "none", overflowY: "visible", overflowX: "auto",
          "&::-webkit-scrollbar":       { height: 8 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.12)", borderRadius: 4 },
        }}
      >
        {loading ? (
          <Table sx={{ minWidth: tableMinWidth }} size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" align="center" sx={{ bgcolor: "background.neutral" }}>
                  <Skeleton variant="rounded" width={18} height={18} />
                </TableCell>
                {visibleDataColumns.map((col) => (
                  <TableCell key={col.key} align={col.align} sx={{ bgcolor: "background.neutral" }}>
                    <Skeleton variant="text" width={col.skeletonWidth} height={20} sx={{ borderRadius: 1 }} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <SkeletonTableRow key={i} rowIdx={i} columns={visibleDataColumns} />
              ))}
            </TableBody>
          </Table>
        ) : filteredAssets.length === 0 ? (
          <Box sx={{ p: 8, textAlign: "center" }}>
            <Alert severity="info" sx={{ justifyContent: "center", borderRadius: 2.5 }}>
              No assets found matching the selected filters.
            </Alert>
            {enrichedAssets.length > 0 && (
              <Button onClick={handleClearAll} sx={{ mt: 2, textTransform: "none" }}>
                Clear Filters
              </Button>
            )}
          </Box>
        ) : (
          <Table sx={{ minWidth: tableMinWidth }} size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" align="center" sx={{ bgcolor: "background.neutral" }}>
                  <Checkbox
                    indeterminate={someCurrentPageSelected}
                    checked={allCurrentPageSelected}
                    onChange={(e) => toggleAllAssetsSelection(e.target.checked)}
                  />
                </TableCell>
                {visibleDataColumns.map((col) => (
                  <TableCell key={col.key} align={col.align} sx={{ bgcolor: "background.neutral", py: 1.5 }}>
                    {col.key !== "actions" ? (
                      <TableSortLabel
                        active={sortField === col.key}
                        direction={sortField === col.key ? sortDirection : "asc"}
                        onClick={() => handleSortClick(col.key)}
                        sx={{
                          fontWeight: 700,
                          color: "text.secondary",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          whiteSpace: "nowrap",
                          flexDirection: "row !important",
                          "&.Mui-active": { color: "text.primary" },
                          "& .MuiTableSortLabel-icon": {
                            color: "text.primary !important",
                            marginLeft: "4px !important",
                            marginRight: "0 !important",
                          },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      <Typography sx={{ fontWeight: 700, color: "text.secondary", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {col.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {currentPageAssets.map((row) => {
                const isAssetSelected = selectedAssets.has(row.id);
                const isExpired       = isWarrantyExpired(row.warrantyExpiry);
                const isExpiring      = isWarrantyExpiring(row.warrantyExpiry, WARRANTY_EXPIRY_DAYS);

                return (
                 <TableRow
  key={row.id}
  hover
  selected={isAssetSelected}
  onMouseEnter={() => setHoveredRowId(row.id)}
  onMouseLeave={() => setHoveredRowId(null)}
>
 <TableCell
  padding="checkbox"
  align="center"
  onClick={(e) => e.stopPropagation()}
>
  <Checkbox
    checked={isAssetSelected}
    onChange={(e) => handleAssetSelection(row.id, e.target.checked)}
  />
</TableCell>

              {visibleColumns.includes("assetId") && (
  <TableCell
    sx={{
      fontFamily: "monospace",
      fontWeight: 600,
      color: "primary.main",
      fontSize: "0.85rem",
      whiteSpace: "nowrap",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minHeight: 28,
      }}
    >
      <span>{row.assetId || "—"}</span>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleMenuOpen(e, row);
        }}
        sx={{
          p: 0.25,
          opacity: hoveredRowId === row.id ? 1 : 0,
          pointerEvents: hoveredRowId === row.id ? "auto" : "none",
          transition: "opacity 0.15s ease",
        }}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  </TableCell>
)}

                    {visibleColumns.includes("assetName") && (
                      <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                        {row.assetName || "—"}
                      </TableCell>
                    )}

                    {visibleColumns.includes("assetType") && (
                      <TableCell>
                      <Chip
  label={
    catalogAssetTypes.find((t) =>
      String(t.value).toLowerCase() === String(row.assetType || "").toLowerCase()
    )?.label || row.assetType || "Primary"
  }
  size="small" variant="outlined"
  color={(() => {
    const t = String(row.assetType || "").toLowerCase();
    if (t === "primary" || t === "primary asset") return "primary";
    if (t === "consumable" || t === "spare part" || t === "accessory") return "warning";
    if (t === "component") return "info";
    if (t === "parent" || t === "parent asset" || t === "sub asset") return "secondary";
    return "default";
  })()}
  sx={{ fontWeight: 500, fontSize: "0.72rem" }}
/>
                      </TableCell>
                    )}

                    {visibleColumns.includes("ownershipType") && (
                      <TableCell>
                      <Chip
  label={
    catalogOwnershipTypes.find((t) =>
      String(t.value).toLowerCase() === String(row.ownershipType || "").toLowerCase()
    )?.label || row.ownershipType || "Owned"
  }
  size="small" variant="outlined"
  color={(() => {
    const o = String(row.ownershipType || "").toLowerCase();
    if (o === "leased") return "info";
    if (o === "rented") return "secondary";
    return "default";
  })()}
  sx={{ fontWeight: 500, fontSize: "0.72rem" }}
/>
                      </TableCell>
                    )}

                    {visibleColumns.includes("parentAsset") && (
                      <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                        {ASSET_TYPES_SHOW_PARENT.includes(row.assetType) ? (
                          row.parentAssetName ? (
                            <Tooltip title={`Parent ID: ${row.parentAssetId || "—"}`} arrow>
                              <Typography variant="caption"
                                sx={{ fontWeight: 600, color: "primary.main", cursor: "default" }}>
                                {row.parentAssetName}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="error.main" fontWeight={500}>
                              No Parent
                            </Typography>
                          )
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    )}

                                       {visibleColumns.includes("category") && (
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                            {row.categoryName || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.subCategoryName || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}

                    {visibleColumns.includes("location") && (
                      <TableCell>
                        {row.locationCode ? (
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                            {row.locationName || row.currentLocationName || row.locationCode}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    )}
                    {visibleColumns.includes("tracking") && (
                      <TableCell>
                        <Chip
                          label={row.serializationType === SERIALIZATION_TYPES.NON_SERIALIZED ? "Qty Bulk" : "Serialized"}
                          size="small"
                          color={row.serializationType === SERIALIZATION_TYPES.NON_SERIALIZED ? "info" : "secondary"}
                          variant="outlined"
                          sx={{ fontWeight: 500, fontSize: "0.72rem" }}
                        />
                      </TableCell>
                    )}

                    {visibleColumns.includes("purchaseDate") && (
                      <TableCell sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                       {(() => {
  const o = String(row.ownershipType || "").toLowerCase();
  if (o === "leased") return formatDate(row.leaseStartDate);
  if (o === "rented") return formatDate(row.rentStartDate);
  return formatDate(row.purchaseDate || row.purchasedDate);
})()}
                      </TableCell>
                    )}

                    {visibleColumns.includes("purchaseValue") && (
                      <TableCell sx={{
                        fontWeight: 600,
                        color: row.purchaseValue || row.leaseAmount || row.rentAmount
                          ? "text.primary" : "text.disabled",
                        fontSize: "0.85rem", fontFamily: "monospace",
                      }}>
{(() => {
  const o = String(row.ownershipType || "").toLowerCase();
  if (o === "leased" && row.leaseAmount) return formatCurrency(row.leaseAmount);
  if (o === "rented" && row.rentAmount)  return formatCurrency(row.rentAmount);
  return row.purchaseValue ? formatCurrency(row.purchaseValue) : "—";
})()}
                      </TableCell>
                    )}

                    {visibleColumns.includes("manufacturerSerial") && (
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "text.secondary" }}>
                        {row.serializationType === SERIALIZATION_TYPES.NON_SERIALIZED
                          ? "—" : row.manufacturerSerialNumber || "—"}
                      </TableCell>
                    )}

                    {visibleColumns.includes("quantity") && (
                      <TableCell align="center" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {row.quantity || 1}{" "}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.3 }}>
                          {row.serializationType === SERIALIZATION_TYPES.NON_SERIALIZED
                            ? row.unitOfMeasurement || "units" : ""}
                        </Typography>
                      </TableCell>
                    )}

                    {visibleColumns.includes("warranty") && (
                      <TableCell>
                        {!row.warrantyExpiry ? (
                          <Typography variant="caption" color="text.disabled" fontWeight="500">
                            Not Set
                          </Typography>
                        ) : isExpired ? (
                          <Chip label="Expired" size="small" color="error"
                            sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                        ) : isExpiring ? (
                          <Chip label="Expiring Soon" size="small" color="warning"
                            sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                        ) : (
                          <Chip
                            label={`Active: ${formatDate(row.warrantyExpiry)}`}
                            size="small" color="success" variant="outlined"
                            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                          />
                        )}
                      </TableCell>
                    )}

                                           {visibleColumns.includes("status") && (
                      <TableCell>
                       <Chip
  label={
    catalogStatuses.find((s) =>
      String(s.value).toLowerCase() === String(row.status || "").toLowerCase()
    )?.label || row.status || "Unknown"
  }
  size="small"
  color={(() => {
    const s = String(row.status || "").toLowerCase();
    if (s === "disposed")  return "error";
    if (s === "assigned" || s === "in-maintenance") return "success";
    if (s === "returned")  return "warning";
    if (s === "lost" || s === "under-repair")       return "error";
    if (s === "reserved")  return "info";
    if (s === "available") return "default";
    return "default";
  })()}
  sx={{ fontWeight: 600, fontSize: "0.72rem" }}
/>
                      </TableCell>
                    )}

                    {/* NEW: Tagged Column */}
                    {visibleColumns.includes("tagged") && (
                      <TableCell align="center">
                        <Chip
                          label={row.isTagged ? "Tagged" : "Untagged"}
                          size="small"
                          color={row.isTagged ? "success" : "default"}
                          variant={row.isTagged ? "filled" : "outlined"}
                          sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                        />
                      </TableCell>
                    )}
                                                                       {visibleColumns.includes("approvalStatus") && (
                      <TableCell>
                        {(() => {
                         const label = getApprovalStatusForAsset(row, svlApprovers);
                          const isNA = label === "NA" || !label;
                          const color = getApprovalStatusColor(label);
                          return (
                            <Chip
                              label={isNA ? "NA" : label}
                              size="small"
                              color={color}
                              variant={isNA ? "outlined" : "filled"}
                              sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                            />
                          );
                        })()}
                      </TableCell>
                    )}

                    {visibleColumns.includes("actions") && (
                      <TableCell align="center">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}

                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* ══ PAGINATION ══════════════════════════════════════════════════════ */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {isSearchActive
              ? `Showing all ${filteredAssets.length} result(s) matching "${searchTerm}"`
              : rowsPerPage === -1
              ? `Showing all ${filteredAssets.length} asset(s)`
              : `Showing ${filteredAssets.length === 0 ? 0 : page * rowsPerPage + 1}–` +
                `${Math.min((page + 1) * rowsPerPage, filteredAssets.length)} ` +
                `of ${filteredAssets.length} assets`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25, flexWrap: "wrap" }}>
            {isSearchActive ? (
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                Pagination suspended — clear search to restore paging
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  Page size: <strong>{pageSizeLabel}</strong>
                </Typography>
                <Tooltip title="Change page size in Preferences" arrow>
                  <IconButton size="small" onClick={handleGoToPreferences}
                    sx={{ p: 0.25, color: "primary.main", "&:hover": { bgcolor: "primary.50" } }}>
                    <SettingsIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }}>
              •
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              Columns: <strong>{visibleDataColumns.length}/{DATA_COLUMNS.length}</strong>
            </Typography>
            <Tooltip title="Manage visible columns" arrow>
              <IconButton size="small" onClick={() => setShowColumnPrefsModal(true)}
                sx={{ p: 0.25, color: "primary.main", "&:hover": { bgcolor: "primary.50" } }}>
                <ViewColumnIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {showPaginationNav && (
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={filteredAssets.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={() => {}}
            sx={{
              border: "none", overflow: "hidden",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-input": { display: "none" },
            }}
          />
        )}
      </Box>

      {/* ══ CONTEXT MENU ════════════════════════════════════════════════════ */}
      <Menu
        anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
        TransitionComponent={Fade}
        PaperProps={{ sx: { minWidth: 240, borderRadius: 2.5, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)" } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
      {canEditAssetRow(menuTargetAsset) ? (
  <MenuItem
    onClick={() => { handleEditClick(menuTargetAsset); handleMenuClose(); }}
    sx={{ gap: 1.5, py: 1 }}
  >
    <EditIcon fontSize="small" color="action" />
    <Typography variant="body2" fontWeight={500}>Edit</Typography>
  </MenuItem>
) : (
  <MenuItem disabled sx={{ gap: 1.5, py: 1 }}>
    <LockIcon fontSize="small" color="disabled" />
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.disabled">
        Edit Locked
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {(() => {
          const s = getApprovalStatusForAsset(menuTargetAsset);
          if (s !== "Draft" && s !== "Returned" && s !== "NA") {
            return `Not Editable`;
          }
          return "Only the owner or bypass-list users can edit";
        })()}
      </Typography>
    </Box>
  </MenuItem>
)}
        {/* ── RAISE HELPDESK TICKET ── */}
        {canRaiseTicketForAsset(menuTargetAsset) && (
          <>
            <MenuItem
              onClick={() => {
                handleRaiseTicketClick(menuTargetAsset);
                handleMenuClose();
              }}
              sx={{ gap: 1.5, py: 1 }}
            >
              <SupportAgentIcon fontSize="small" sx={{ color: "#7c3aed" }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="#6d28d9">
                  Raise Ticket
                </Typography>
                <Typography
                  variant="caption" color="text.secondary"
                  display="block" lineHeight={1.2}
                >
                  Report issue & resolve for this asset
                </Typography>
              </Box>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
          </>
        )}
        {actionsAllowedByApproval && !menuTargetIsAssigned && String(menuTargetAsset?.status || "").toLowerCase() !== "disposed" && (
          <MenuItem onClick={() => { handleReassignClick(menuTargetAsset); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
            <AssignmentIndIcon fontSize="small" sx={{ color: "success.main" }} />
            <Box>
              <Typography variant="body2" fontWeight={600} color="success.dark">
                Assign Asset
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                Assign to a user or department
              </Typography>
            </Box>
          </MenuItem>
        )}

        {actionsAllowedByApproval && menuTargetIsAssigned && (
          <MenuItem onClick={() => { handleReassignClick(menuTargetAsset); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
            <AssignmentIndIcon fontSize="small" sx={{ color: "info.main" }} />
            <Box>
              <Typography variant="body2" fontWeight={600} color="info.dark">
                Reassign / Release
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                Currently: {menuTargetAsset?.assignedToName || menuTargetAsset?.assignedTo || "—"}
              </Typography>
            </Box>
          </MenuItem>
        )}

               {actionsAllowedByApproval && menuTargetAsset?.serializationType === SERIALIZATION_TYPES.NON_SERIALIZED && (
          <MenuItem onClick={() => { handleQuantityAdjustClickLocal(menuTargetAsset); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>     <UpdateIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>Adjust Quantity</Typography>
          </MenuItem>
        )}
        {actionsAllowedByApproval && (
          <MenuItem onClick={() => { handleQRClick(menuTargetAsset); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
            <QrCodeIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>View QR Code</Typography>
          </MenuItem>
        )}
          {actionsAllowedByApproval && (!menuTargetAsset?.ownershipType ||
  String(menuTargetAsset.ownershipType).toLowerCase() === "owned") && (
          <MenuItem onClick={() => { handleExtendWarrantyClick(menuTargetAsset); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>      <HistoryIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>Extend Warranty</Typography>
          </MenuItem>
        )}

        {menuTargetAsset?.invoiceFileUrl && (
          <MenuItem onClick={() => { handleViewInvoice(menuTargetAsset.invoiceFileUrl); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
            <DescriptionIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>View Invoice</Typography>
          </MenuItem>
        )}


        {menuTargetAsset?.disposalHistory?.length > 0 && (
          <MenuItem
            onClick={() => {
              handleDisposalHistoryClick(menuTargetAsset);
              handleMenuClose();
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <HistoryIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>Disposal History</Typography>
          </MenuItem>
        )}
        {Array.isArray(menuTargetAsset?.approvalHistory) &&
          menuTargetAsset.approvalHistory.length > 0 && (
          <MenuItem
            onClick={() => {
              handleApprovalHistoryClick(menuTargetAsset);
              handleMenuClose();
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <HowToRegIcon fontSize="small" sx={{ color: "info.main" }} />
            <Box>
              <Typography variant="body2" fontWeight={600} color="info.dark">
                Approval History
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                {menuTargetAsset.approvalHistory.length} action{menuTargetAsset.approvalHistory.length !== 1 ? "s" : ""} recorded
              </Typography>
            </Box>
          </MenuItem>
        )}
                {actionsAllowedByApproval && canRecordConsumption(menuTargetAsset?.assetType) && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={() => { handleStockTransactionClick(menuTargetAsset, "Receipt"); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
              <SwapHorizIcon fontSize="small" sx={{ color: "primary.main" }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="primary.dark">
                  Stock Transaction
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                  Receipt (add stock) or Issue (deduct stock)
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={() => { handleRecordConsumptionClick(menuTargetAsset); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}>
              <InventoryIcon fontSize="small" sx={{ color: "warning.main" }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="warning.dark">
                  Record Consumption
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                  Log usage against a work order
                </Typography>
              </Box>
            </MenuItem>
          </>
        )}

        <Divider sx={{ my: 0.5 }} />

             {actionsAllowedByApproval && isReturnOnlyAsset(menuTargetAsset?.ownershipType) && (
          <MenuItem onClick={() => { handleReturnClick(menuTargetAsset); handleMenuClose(); }} sx={{ gap: 1.5, py: 1, color: "warning.dark" }}>    <AssignmentReturnIcon fontSize="small" color="warning" />
            <Typography variant="body2" fontWeight={600}>Return Asset</Typography>
          </MenuItem>
        )}

              {actionsAllowedByApproval && canBeDisposed(menuTargetAsset?.assetType) && !isReturnOnlyAsset(menuTargetAsset?.ownershipType) && (
          <MenuItem onClick={() => { handleDisposeClick(menuTargetAsset); handleMenuClose(); }} sx={{ gap: 1.5, py: 1, color: "error.main" }}>   <DeleteSweepIcon fontSize="small" color="error" />
            <Typography variant="body2" fontWeight={600}>Dispose Asset</Typography>
          </MenuItem>
        )}

             {canDeleteAssetRow(menuTargetAsset) ? (
          <MenuItem
            onClick={() => { handleDeleteClick(menuTargetAsset); handleMenuClose(); }}
            sx={{ gap: 1.5, py: 1, color: "error.main" }}
          >
            <DeleteIcon fontSize="small" color="error" />
            <Typography variant="body2" fontWeight={600}>Delete</Typography>
          </MenuItem>
        ) : (
          <MenuItem disabled sx={{ gap: 1.5, py: 1 }}>
            <LockIcon fontSize="small" color="disabled" />
            <Box>
              <Typography variant="body2" fontWeight={600} color="text.disabled">
                Delete Locked
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Admin only — asset is {getApprovalStatusForAsset(menuTargetAsset)}
              </Typography>
            </Box>
          </MenuItem>
        )}

        {!actionsAllowedByApproval && (
          <MenuItem disabled sx={{ gap: 1.5, py: 1 }}>
            <LockIcon fontSize="small" color="disabled" />
            <Box>
              <Typography variant="body2" fontWeight={600} color="text.disabled">
                Actions Locked
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Asset Under Approval
              </Typography>
            </Box>
          </MenuItem>
        )}
      </Menu>

      {/* ══ TOAST ════════════════════════════════════════════════════════════ */}
      {notification && (
        <ToastNotification message={notification.message} type={notification.type} />
      )}

      {/* ══ MODALS ══════════════════════════════════════════════════════════ */}

      <ColumnPreferencesModal
        open={showColumnPrefsModal}
        onClose={() => setShowColumnPrefsModal(false)}
        visibleColumns={visibleColumns}
        onToggleColumn={toggleColumn}
        onShowAll={showAllColumns}
        onHideAll={hideAllOptionalColumns}
        onReset={resetColumns}
      />

      {(showCreateModal || showEditModal) && (
<CreateEditAssetModal
  open={showCreateModal || showEditModal}
  asset={showEditModal ? selectedAsset : null}
  onClose={() => { setShowCreateModal(false); setShowEditModal(false); setSelectedAsset(null); }}
  onSave={(updatedAssetData) => {
    if (showEditModal && updatedAssetData) handleAssetUpdated(updatedAssetData);
    else handleAssetCreated();
  }}
  onError={(msg, type) => showNotification(msg, type)}
  currentUser={currentUser}
  categories={masterCategories}
  subCategories={masterSubCategories}
  brands={masterBrands}
  vendors={masterVendors}
  departments={masterDepartments}
  users={systemUsers}
  locations={masterLocations}
  preferencesVersion={prefsVersion}  
    />
      )}
{showImportModal && (
  <AssetImportModal
    open={showImportModal}
    onClose={() => setShowImportModal(false)}
    currentUser={currentUser}
    onSuccess={() => {
      setShowImportModal(false);
      fetchAssets();
      showNotification("Assets imported successfully", "success");
    }}
  />
)}

      {showDeleteModal && selectedAsset && (
        <DeleteConfirmationModal
          show={showDeleteModal} asset={selectedAsset}
          onClose={() => { setShowDeleteModal(false); setSelectedAsset(null); }}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showQRModal && selectedQRAsset && (
        <AssetQRModal
          asset={selectedQRAsset}
          onClose={() => { setShowQRModal(false); setSelectedQRAsset(null); }}
        />
      )}

      {showExtendWarrantyModal && selectedWarrantyAsset && (
        <ExtendWarrantyModal
          show={showExtendWarrantyModal} asset={selectedWarrantyAsset}
          onClose={() => { setShowExtendWarrantyModal(false); setSelectedWarrantyAsset(null); }}
          onExtend={handleWarrantyExtended}
        />
      )}

      {/* DisposeAssetModal — consolidated with history tab */}
      {showDisposeModal && selectedDisposeAsset && (
        <DisposeAssetModal
          open={showDisposeModal}
          asset={selectedDisposeAsset}
          initialTab={disposeModalInitialTab}
          currentUser={currentUser}
          onClose={() => {
            setShowDisposeModal(false);
            setSelectedDisposeAsset(null);
            setDisposeModalInitialTab('dispose');
          }}
          onSave={handleAssetDisposed}
          onError={(msg, type = "error") => showNotification(msg, type)}
        />
      )}

     

      <QRPrintModal
        isOpen={showQRPrintModal} selectedAssets={assetsForPrint}
        onClose={closeQRPrintModal} onNotification={showNotification}
      />

      {showQuantityModal && selectedQuantityAsset && (
        <QuantityAdjustmentModal
          show={showQuantityModal} asset={selectedQuantityAsset}
          onClose={() => { setShowQuantityModal(false); setSelectedQuantityAsset(null); }}
          onUpdateQuantity={updateAssetQuantity}
        />
      )}

      {showReturnModal && selectedReturnAsset && (
        <ReturnAssetModal
          open={showReturnModal}
          asset={selectedReturnAsset}
          currentUser={currentUser}
          onClose={() => {
            setShowReturnModal(false);
            setSelectedReturnAsset(null);
          }}
          onSave={handleAssetReturned}
          onError={(msg, type = "error") => showNotification(msg, type)}
        />
      )}

      {showStockModal && selectedStockAsset && (
        <ConsumableStockModal
          open={showStockModal} asset={selectedStockAsset}
          currentUser={currentUser}
          defaultTransactionType={selectedStockAsset._defaultTransactionType || "Receipt"}
          onClose={() => { setShowStockModal(false); setSelectedStockAsset(null); }}
          onSuccess={(result) => {
            showNotification(
              `${result.transactionType} recorded successfully. New stock: ${result.stockAfter} ${result.uom}.`,
              result.transactionType === "Receipt" ? "success" : "warning"
            );
            fetchAssets();
          }}
        />
      )}

          {showReassignModal && selectedReassignAsset && (
        <AssignAssetModal
          open={showReassignModal}
          asset={selectedReassignAsset}
          currentUser={currentUser}
          users={systemUsers}
          departments={masterDepartments}
          onClose={() => { setShowReassignModal(false); setSelectedReassignAsset(null); }}
          onSave={(updatedAsset) => { handleAssetReassigned(updatedAsset); }}
          onError={(message, type = "error") => { showNotification(message, type); }}
        />
      )}

      {showApprovalHistoryModal && selectedApprovalAsset && (
        <ApprovalHistoryModal
          open={showApprovalHistoryModal}
          asset={selectedApprovalAsset}
          onClose={() => {
            setShowApprovalHistoryModal(false);
            setSelectedApprovalAsset(null);
          }}
        />
      )}
      {/* ══ HELPDESK TICKET FORM (ADMIN) ══════════════════════════════════ */}
            
      {showTicketModal && ticketTargetAsset && (
        <NewTicketForm
          open={showTicketModal}
          role={ROLES.ADMIN}
          lockAsset={true}  
          onClose={handleTicketClose}
          onSuccess={handleTicketSuccess}
          initialAsset={{
            assetId:   ticketTargetAsset.assetId,
            assetName: ticketTargetAsset.assetName,
            type:      ticketTargetAsset.assetType,
            location:  ticketTargetAsset.locationName
                    || ticketTargetAsset.currentLocationName
                    || "",
          }}
        />
      )}
    </Container>
  );
};

export default AssetManagement;